// ============================================================
//  src/pages/bots/tabs/FlowsTab.js
//  WhatsApp Flows: list, create, JSON editor (CodeMirror) with live
//  phone preview, local + Meta validation, endpoint setup + health
//  check, encryption keys and traffic logs.
//  Styles: ./FlowsTab.css (scoped under .flx-*)
// ============================================================
import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { copyToClipboard, formatDate } from '../../../utils/helpers';
import './FlowsTab.css';

// Coerces a value that's SUPPOSED to be an array (e.g. a component's
// "data-source") into one safely. Malformed flow JSON that's technically
// still valid JSON -- e.g. "data-source": "oops" instead of an array --
// used to reach a bare .map() call and throw mid-render, which (with no
// Error Boundary anywhere in this tree) unmounted the entire page. This
// is the actual fix for that specific crash; ErrorBoundary below is the
// safety net for anything else along the same lines.
function asArray(val) {
  return Array.isArray(val) ? val : [];
}

// React only stops an uncaught render error from unmounting the whole
// tree if something implements componentDidCatch/getDerivedStateFromError
// -- there was nothing here before, so ANY unexpected shape in a user's
// pasted flow.json (not just the data-source case above) could blank the
// entire page with no way back except reloading and hoping unsaved edits
// survived. This scopes that blast radius to just the panel it wraps.
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[FlowsTab] Preview render error:', error, info);
  }
  render() {
    if (this.state.error) {
      return (
        <div className="flx-prev-empty is-crash">
          <div className="flx-prev-empty-phone"><i className="fa-solid fa-triangle-exclamation" /></div>
          <b>Couldn’t render this preview</b>
          <span>
            Something in the flow JSON has an unexpected shape (e.g. a field that should be a list isn’t one).
            Your code is safe — check the <strong>Errors</strong> tab, or the browser console for details.
          </span>
          <button type="button" className="flx-btn ghost sm" onClick={() => this.setState({ error: null })}>
            <i className="fa-solid fa-rotate" /> Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const API_BASE = process.env.REACT_APP_API_URL || '/api';

async function flowsApi(bId, body) {
  const token = localStorage.getItem('sAuthToken');
  const res = await fetch(`${API_BASE}/bots/flows/?bId=${encodeURIComponent(bId)}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(body),
  });
  return res.json();
}

async function flowsGet(bId) {
  const token = localStorage.getItem('sAuthToken');
  const res = await fetch(`${API_BASE}/bots/flows/?bId=${encodeURIComponent(bId)}`, {
    credentials: 'include',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  return res.json();
}

const CATEGORIES = [
  'OTHER', 'SIGN_UP', 'SIGN_IN', 'APPOINTMENT_BOOKING',
  'LEAD_GENERATION', 'CONTACT_US', 'CUSTOMER_SUPPORT', 'SURVEY', 'SHOPPING',
];

const STATUS_META = {
  draft:      { tone: 'draft', label: 'Draft' },
  published:  { tone: 'live',  label: 'Live' },
  deprecated: { tone: 'dep',   label: 'Deprecated' },
};

// Components that require certain fields. Keys are lowercased type names.
const COMPONENT_RULES = {
  textinput:        { required: ['name', 'label'] },
  'text-input':     { required: ['name', 'label'] },
  textarea:         { required: ['name', 'label'] },
  radiobuttons:     { required: ['name', 'data-source'] },
  'radio-buttons':  { required: ['name', 'data-source'] },
  checkboxes:       { required: ['name', 'data-source'] },
  'checkbox-group': { required: ['name', 'data-source'] },
  dropdown:         { required: ['name', 'data-source'] },
  datepicker:       { required: ['name'] },
  'date-picker':    { required: ['name'] },
  image:            { required: ['src'] },
};

// Every component "type" value this file already knows how to render,
// UNIONED with real Meta WhatsApp Flow component types this preview
// doesn't render yet (RichText, CalendarPicker, OptIn, etc.) -- those
// aren't errors, just not drawn specially (they fall through to a
// generic placeholder box). This set exists purely to catch genuinely
// invalid values -- typos, made-up names -- the same class of mistake
// Meta's own validator flags as "Invalid value found for property
// 'type'." Kept deliberately permissive (both hyphenated and
// non-hyphenated spellings) to match this file's own existing tolerance
// rather than introduce new false positives.
const VALID_COMPONENT_TYPES = new Set([
  'textheading', 'text-heading',
  'textsubheading', 'text-subheading',
  'textbody', 'text-body', 'text',
  'textcaption', 'text-caption',
  'richtext', 'rich-text',
  'textinput', 'text-input',
  'textarea',
  'checkboxgroup', 'checkbox-group', 'checkboxes',
  'radiobuttonsgroup', 'radio-buttons-group', 'radiobuttons', 'radio-buttons',
  'dropdown',
  'datepicker', 'date-picker',
  'calendarpicker', 'calendar-picker',
  'optin', 'opt-in',
  'chipsselector', 'chips-selector',
  'image',
  'imagecarousel', 'image-carousel',
  'photopicker', 'photo-picker',
  'documentpicker', 'document-picker',
  'footer',
  'embeddedlink', 'embedded-link',
  'navigationlist', 'navigation-list',
  'form', 'if', 'switch',
]);

function levenshtein(a, b) {
  const m = a.length, n = b.length;
  const dp = Array.from({ length: m + 1 }, (_, i) => [i, ...Array(n).fill(0)]);
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i - 1] === b[j - 1]
        ? dp[i - 1][j - 1]
        : 1 + Math.min(dp[i - 1][j - 1], dp[i - 1][j], dp[i][j - 1]);
    }
  }
  return dp[m][n];
}

// Best-effort "did you mean X?" for a misspelled component type --
// Meta's own error doesn't offer this, but it's cheap and genuinely
// useful given typos (e.g. "TextInptu") are the single most common
// version of this exact error.
const CANONICAL_TYPE_NAMES = [
  'TextHeading', 'TextSubheading', 'TextBody', 'TextCaption', 'RichText',
  'TextInput', 'TextArea', 'CheckboxGroup', 'RadioButtonsGroup', 'Dropdown',
  'DatePicker', 'CalendarPicker', 'OptIn', 'ChipsSelector', 'Image',
  'ImageCarousel', 'PhotoPicker', 'DocumentPicker', 'Footer', 'EmbeddedLink',
  'NavigationList', 'Form', 'If', 'Switch',
];
// Properties allowed per component type, for flagging things like
// "Property 'color' is not allowed in 'TextBody' component." -- kept
// deliberately scoped to the text/display components (plus a few simple
// structural ones) where the real Meta schema is small and stable
// enough to be confident about. Input/selection components have larger,
// more varied property sets; getting one wrong there risks a false
// positive that's worse than not checking at all, so this intentionally
// does not attempt to cover every component type.
const ALLOWED_PROPS = {
  textheading:     ['type', 'text', 'visible'],
  'text-heading':  ['type', 'text', 'visible'],
  textsubheading:    ['type', 'text', 'visible'],
  'text-subheading': ['type', 'text', 'visible'],
  textbody:    ['type', 'text', 'visible', 'font-weight', 'strikethrough', 'markdown'],
  'text-body': ['type', 'text', 'visible', 'font-weight', 'strikethrough', 'markdown'],
  text:        ['type', 'text', 'visible', 'font-weight', 'strikethrough', 'markdown'],
  textcaption:    ['type', 'text', 'visible', 'font-weight', 'strikethrough', 'markdown'],
  'text-caption': ['type', 'text', 'visible', 'font-weight', 'strikethrough', 'markdown'],
  richtext:    ['type', 'text', 'visible'],
  'rich-text': ['type', 'text', 'visible'],
  footer: ['type', 'label', 'left-caption', 'center-caption', 'right-caption', 'on-click-action', 'enabled', 'visible'],
  embeddedlink:    ['type', 'text', 'on-click-action', 'visible'],
  'embedded-link': ['type', 'text', 'on-click-action', 'visible'],
  image: ['type', 'src', 'width', 'height', 'scale-type', 'aspect-ratio', 'visible', 'alt-text'],
  // Added after a real Meta validator response confirmed "placeholder"
  // is NOT accepted here (a very easy mistake -- "label" is the field
  // that plays that role in WhatsApp Flow's TextInput, not
  // "placeholder" like most other UI frameworks use).
  textinput:    ['type', 'name', 'label', 'input-type', 'required', 'visible', 'helper-text', 'error-message', 'min-chars', 'max-chars', 'pattern', 'init-value', 'enabled'],
  'text-input': ['type', 'name', 'label', 'input-type', 'required', 'visible', 'helper-text', 'error-message', 'min-chars', 'max-chars', 'pattern', 'init-value', 'enabled'],
};

// Meta's dynamic "visible" only accepts a bare single-field reference --
// ${data.x} or ${screen.data.x} -- NOT a compound expression with
// operators (${data.x == y}, ${data.list.length > 0}, etc.), even
// though those look like reasonable boolean expressions. Anything that
// isn't a literal boolean or this exact shape gets rejected by Meta as
// a plain string, which is what produces its "expected boolean but
// found string" message despite the value visually looking dynamic.
const VISIBLE_DYNAMIC_RE = /^\$\{(screen\.)?data\.[a-zA-Z0-9_]+\}$/;

function suggestComponentType(badType) {
  if (!badType) return null;
  const lower = badType.toLowerCase();
  let best = null, bestDist = Infinity;
  for (const name of CANONICAL_TYPE_NAMES) {
    const d = levenshtein(lower, name.toLowerCase());
    if (d < bestDist) { bestDist = d; best = name; }
  }
  // Only suggest if it's plausibly a typo, not a totally unrelated word
  return (best && bestDist <= Math.max(3, Math.ceil(badType.length * 0.4))) ? best : null;
}

// ── Position-tracking JSON parser ───────────────────────────────
//  A minimal hand-rolled JSON parser that records {line, column} for
//  the start of every key and value it parses, alongside the normal
//  parsed value. Native JSON.parse gives no way to recover this, and
//  it's what makes it possible to turn a lint issue's object path
//  (e.g. "screens[1].layout.children[3].type", built by walking the
//  already-parsed object) back into a real position in the raw text --
//  instantly, client-side, matching the exact [Line X, Column Y] format
//  Meta's own validator returns from a real server round trip.
function parseJsonWithPositions(text) {
  let i = 0;
  const len = text.length;
  let line = 1, col = 1;

  function advance(n = 1) {
    for (let k = 0; k < n; k++) {
      if (i < len && text[i] === '\n') { line++; col = 1; } else { col++; }
      i++;
    }
  }
  function skipWs() {
    while (i < len && /\s/.test(text[i])) advance();
  }
  function pos() { return { line, column: col }; }

  function parseValue() {
    skipWs();
    const start = pos();
    const ch = text[i];
    let value, positions = null;
    if (ch === '{') {
      const r = parseObject(); value = r.value; positions = r.positions;
    } else if (ch === '[') {
      const r = parseArray(); value = r.value; positions = r.positions;
    } else if (ch === '"') {
      value = parseString();
    } else if (text.startsWith('true', i)) {
      value = true; advance(4);
    } else if (text.startsWith('false', i)) {
      value = false; advance(5);
    } else if (text.startsWith('null', i)) {
      value = null; advance(4);
    } else {
      const m = /^-?\d+(\.\d+)?([eE][+-]?\d+)?/.exec(text.slice(i));
      if (!m) throw new Error(`Unexpected token at line ${line}, column ${col}`);
      value = Number(m[0]);
      advance(m[0].length);
    }
    return { value, start, positions };
  }

  function parseString() {
    advance(); // opening quote
    let s = '';
    while (i < len && text[i] !== '"') {
      if (text[i] === '\\') {
        const next = text[i + 1];
        if (next === 'u') {
          s += String.fromCharCode(parseInt(text.slice(i + 2, i + 6), 16));
          advance(6);
        } else {
          s += ({ n: '\n', t: '\t', r: '\r', '"': '"', '\\': '\\', '/': '/', b: '\b', f: '\f' })[next] ?? next;
          advance(2);
        }
      } else {
        s += text[i];
        advance();
      }
    }
    advance(); // closing quote
    return s;
  }

  function parseObject() {
    const positions = {};
    const value = {};
    advance(); // {
    skipWs();
    if (text[i] === '}') { advance(); return { value, positions }; }
    while (i < len) {
      skipWs();
      const keyStart = pos();
      const key = parseString();
      skipWs();
      advance(); // :
      const { value: v, start: valueStart, positions: childPositions } = parseValue();
      value[key] = v;
      positions[key] = { keyStart, valueStart, children: childPositions };
      skipWs();
      if (text[i] === ',') { advance(); continue; }
      break;
    }
    skipWs();
    advance(); // }
    return { value, positions };
  }

  function parseArray() {
    const positions = [];
    const value = [];
    advance(); // [
    skipWs();
    if (text[i] === ']') { advance(); return { value, positions }; }
    while (i < len) {
      const { value: v, start: valueStart, positions: childPositions } = parseValue();
      value.push(v);
      positions.push({ valueStart, children: childPositions });
      skipWs();
      if (text[i] === ',') { advance(); skipWs(); continue; }
      break;
    }
    skipWs();
    advance(); // ]
    return { value, positions };
  }

  return parseValue(); // { value, start, positions }
}

// Walks a position tree using the same "screens[1].layout.children[3].type"
// style path lint issues already carry, returning {line, column} for that
// exact key (or its value, if preferValue is set — see below). Returns
// null if the path can't be resolved (e.g. the JSON was edited between
// parses) -- callers should treat that as "no location available"
// rather than fail.
//
// preferValue matters for exact parity with Meta's own column numbers:
// Meta points at the KEY's position for "property not allowed" and
// "invalid value for 'type'" (the problem IS that key/field), but at
// the VALUE's position for "expected type X but found Y" (the problem
// is specifically what's on the right of the colon).
function resolvePathPosition(rootPositions, path, preferValue = false) {
  if (!rootPositions || !path) return null;
  const tokens = path.match(/[^.[\]]+|\[\d+\]/g) || [];
  let node = { children: rootPositions };
  for (const tok of tokens) {
    if (!node || !node.children) return null;
    if (tok.startsWith('[')) {
      const idx = parseInt(tok.slice(1, -1), 10);
      node = node.children[idx];
    } else {
      node = node.children[tok];
    }
  }
  if (!node) return null;
  return preferValue
    ? (node.valueStart ?? node.keyStart ?? null)
    : (node.keyStart ?? node.valueStart ?? null);
}

// Field names WhatsApp's own data_exchange protocol already uses at the
// top level of what a screen submits (action, screen, flow_token, version)
// or reserves in the payload (data). A form field named exactly one of
// these gets silently merged into/overwritten by the protocol's own field
// of the same name when the screen submits — the field never reaches your
// endpoint with the value the user actually entered.
const RESERVED_FIELD_NAMES = ['action', 'screen', 'data', 'flow_token', 'version'];

// Component types whose text content can contain ${data.xxx} placeholders
// and are subject to Meta's known limitation: a single string with more
// than one placeholder often only resolves the first one, leaving the
// rest as literal "${data.xxx}" text in what the user sees.
const TEXT_BEARING_TYPES = new Set([
  'textbody', 'text-body', 'text', 'textheading', 'text-heading',
  'textsubheading', 'text-subheading', 'richtext', 'rich-text',
]);
const PLACEHOLDER_RE = /\$\{data\.[^}]+\}/g;

// Meta can report has_key: true (or a key_fingerprint) while
// business_public_key_signature_status says the key on file is broken --
// e.g. 'MISMATCH' means Meta has *something* registered, but it doesn't
// verify, so flows relying on it will fail. Treat those statuses as NOT
// done so the Set Up / Replace Key option stays visible instead of being
// hidden just because Meta has *a* key, working or not.
const UNHEALTHY_KEY_STATUSES = ['MISMATCH', 'mismatch'];
function isEncryptionKeyHealthy(data) {
  if (!data) return false;
  // local_key_valid is only present once the backend has been updated to
  // include it (see FlowController::get_encryption_status). Treat it as
  // healthy-by-default (undefined -> true) so this doesn't regress older
  // API responses -- but once present, a false here means our own stored
  // private key doesn't even parse, regardless of what Meta reports.
  if (data.local_key_valid === false) return false;
  if (UNHEALTHY_KEY_STATUSES.includes(data.key_status)) return false;
  return !!data.has_key ||
    ['ACTIVE', 'active', 'ENABLED', 'enabled'].includes(data.key_status) ||
    !!data.key_fingerprint;
}

/**
 * Lightweight, local schema lint for a WhatsApp Flow JSON definition.
 * This is NOT a substitute for Meta's server-side validation, but catches
 * common structural mistakes instantly without a round trip.
 * Returns an array of { message, path } objects (empty if no issues found).
 */
function lintFlowJson(parsed) {
  const issues = [];
  if (!parsed || typeof parsed !== 'object') return issues;

  if (!parsed.version) {
    issues.push({ message: 'Missing required top-level "version" field', path: 'version' });
  }

  const screens = Array.isArray(parsed.screens) ? parsed.screens : null;
  if (!screens) {
    issues.push({ message: 'Missing or invalid top-level "screens" array', path: 'screens' });
    return issues;
  }
  if (screens.length === 0) {
    issues.push({ message: '"screens" array is empty — a flow needs at least one screen', path: 'screens' });
  }

  const screenIds = new Set();
  screens.forEach((screen, sIdx) => {
    const path = `screens[${sIdx}]`;
    if (!screen.id) {
      issues.push({ message: 'Screen is missing required "id" field', path });
    } else if (screenIds.has(screen.id)) {
      issues.push({ message: `Duplicate screen id "${screen.id}"`, path: `${path}.id` });
    } else {
      screenIds.add(screen.id);
    }
    if (!screen.title) {
      issues.push({ message: 'Screen is missing a "title" field', path: `${path}.title` });
    }
    if (!screen.layout || !Array.isArray(screen.layout.children)) {
      issues.push({ message: 'Screen is missing "layout.children" array', path: `${path}.layout` });
      return;
    }
    walkComponents(screen.layout.children, `${path}.layout.children`, issues, parsed);

    // ── Screen-level structural rules (not per-component) ──────
    // Meta enforces some rules across a whole screen's tree, not on any
    // single component in isolation -- "at most one Footer per screen"
    // is the most common one to hit by accident (e.g. adding a second
    // "Search" button as its own Footer instead of a Form action).
    // Matches Meta's own message and pointing convention exactly: it
    // flags each EXTRA Footer's own ".type", not the screen as a whole.
    const footerPaths = collectComponentPaths(screen.layout.children, `${path}.layout.children`, 'footer');
    if (footerPaths.length > 1) {
      footerPaths.slice(1).forEach(fp => {
        issues.push({
          message: `Maximum number of Footer allowed per screen is 1 but found ${footerPaths.length}.`,
          path: `${fp}.type`,
        });
      });
    }
  });

  // Cross-check terminal screen
  const terminals = screens.filter(s => s.terminal === true);
  if (screens.length > 0 && terminals.length === 0) {
    issues.push({ message: 'No screen marked as "terminal": true — the flow needs an exit screen', path: 'screens' });
  }

  return issues;
}

// Recursively finds every component of a given type (case-insensitive)
// within a subtree, returning each one's exact path -- e.g. for
// checking Meta's "at most one Footer per screen" rule, which needs to
// know about every Footer's location, not just whether one exists.
// Formats every current error (local lint + Meta's own server-validated
// ones, whichever are present) into copyable text, grouping identical
// messages together with each occurrence's path/location listed
// underneath -- the same convention Meta's own error display uses,
// useful for pasting into a bug report or support ticket.
function buildErrorsText({ lintBlocking, lintWarnings, metaErrors }) {
  const groups = new Map(); // message -> [{ path, line, column }]
  function add(message, path, line, column) {
    if (!groups.has(message)) groups.set(message, []);
    groups.get(message).push({ path, line, column });
  }

  (lintBlocking || []).forEach(e => add(e.message, e.path, e.line, e.column));
  (lintWarnings || []).forEach(e => add(`⚠ ${e.message}`, e.path, e.line, e.column));
  (metaErrors || []).forEach(e => add(
    e.error_message ?? e.message ?? 'Unknown error',
    e.path, e.line_start, e.column_start
  ));

  const lines = [];
  for (const [message, occurrences] of groups) {
    lines.push(message);
    occurrences.forEach(o => {
      const loc = o.line ? ` [Line ${o.line}, Column ${o.column ?? '?'}]` : '';
      lines.push(`* ${o.path ?? '(no path)'}${loc}`);
    });
    lines.push('');
  }
  return lines.join('\n').trim();
}

function collectComponentPaths(children, basePath, targetType) {
  let paths = [];
  (children || []).forEach((c, i) => {
    const path = `${basePath}[${i}]`;
    if ((c?.type ?? '').toLowerCase() === targetType) paths.push(path);
    if (Array.isArray(c?.children)) {
      paths = paths.concat(collectComponentPaths(c.children, `${path}.children`, targetType));
    }
  });
  return paths;
}

function walkComponents(children, basePath, issues, root) {
  children.forEach((comp, cIdx) => {
    const path = `${basePath}[${cIdx}]`;
    const type = (comp.type ?? '').toLowerCase();
    if (!comp.type) {
      issues.push({ message: 'Component is missing "type" field', path });
      return;
    }

    // ── Invalid / misspelled component type ───────────────────
    // Matches Meta's own validator's exact wording for this exact
    // mistake, computed instantly and locally instead of waiting on a
    // round trip to Meta's servers.
    if (typeof comp.type === 'string' && !VALID_COMPONENT_TYPES.has(type)) {
      const suggestion = suggestComponentType(comp.type);
      issues.push({
        message: "Invalid value found for property 'type'."
          + (suggestion ? ` Did you mean "${suggestion}"?` : ''),
        path: `${path}.type`,
      });
    }

    const rule = COMPONENT_RULES[type];
    if (rule) {
      rule.required.forEach(field => {
        if (comp[field] === undefined || comp[field] === null || comp[field] === '') {
          issues.push({ message: `"${comp.type}" component is missing required "${field}"`, path: `${path}.${field}` });
        }
      });
    }

    // ── Property not allowed on this component type ────────────
    // Only enforced for the component types in ALLOWED_PROPS (the
    // stable, well-known ones) -- see that constant's comment for why
    // input/selection components are deliberately excluded.
    const allowed = ALLOWED_PROPS[type];
    if (allowed) {
      Object.keys(comp).forEach(key => {
        if (!allowed.includes(key)) {
          issues.push({
            message: `Property '${key}' is not allowed in '${comp.type}' component.`,
            path: `${path}.${key}`,
          });
        }
      });
    }

    // ── "visible" must be a real boolean or a bare data reference ──
    // Meta only accepts ${data.x} / ${screen.data.x} here -- NOT a
    // compound expression with operators, even though something like
    // ${data.count > 0} looks like valid dynamic logic. Anything that
    // doesn't match is treated as a plain string by Meta's own
    // validator, which is what produces both of these messages (yes,
    // Meta really does return both for the same violation).
    if ('visible' in comp) {
      const v = comp.visible;
      const isRealBoolean = typeof v === 'boolean';
      const isBareDataRef = typeof v === 'string' && VISIBLE_DYNAMIC_RE.test(v);
      if (!isRealBoolean && !isBareDataRef) {
        issues.push({
          message: "Expected property 'visible' to be of type 'boolean' but found 'string'.",
          path: `${path}.visible`,
          preferValue: true,
        });
        issues.push({
          message: "Property 'visible' should be of type 'boolean' or have dynamic data format of the form ${screen.data.your_value} or ${data.your_value}.",
          path: `${path}.visible`,
          preferValue: true,
        });
      }
    }

    // ── init-value on the wrong node ──────────────────────────
    // Meta's own examples sometimes show "init-value" directly on an
    // input component (TextInput, DatePicker, etc.) — but that's not
    // where the platform actually reads it from. Initial values belong
    // in the parent Form's "init-values" object, keyed by field name:
    //   { "type": "Form", "init-values": { "your_field": "..." }, ... }
    // A stray "init-value" here is silently ignored, not an error, so
    // the field just renders empty — no signal from Meta at all.
    if (type !== 'form' && comp['init-value'] !== undefined) {
      issues.push({
        severity: 'error',
        message: `"init-value" on a ${comp.type} is ignored here — set initial values on the parent Form instead: {"init-values": {"${comp.name ?? '<name>'}": ...}}`,
        path: `${path}.init-value`,
      });
    }

    // ── Field name collides with WhatsApp's own protocol field ─
    // A form field literally named "action", "screen", "data", etc.
    // shares a name with something the data_exchange payload already
    // uses at the top level — your endpoint can end up reading the
    // protocol's value instead of what the user typed, with nothing
    // in Meta's response to explain why the field is "wrong".
    if (typeof comp.name === 'string' && RESERVED_FIELD_NAMES.includes(comp.name)) {
      issues.push({
        severity: 'error',
        message: `Field name "${comp.name}" collides with a WhatsApp protocol field of the same name — rename it (e.g. "${comp.name}_value") to avoid it being overwritten on submit`,
        path: `${path}.name`,
      });
    }

    // ── Multiple ${data.xxx} placeholders in one text field ────
    // Known Meta limitation: a single string with 2+ placeholders often
    // only resolves the first one, leaving the rest as literal
    // "${data.xxx}" text on the user's screen — not a JSON error, so it
    // only shows up as garbled text after publishing and testing live.
    if (TEXT_BEARING_TYPES.has(type) && typeof comp.text === 'string') {
      const matches = comp.text.match(PLACEHOLDER_RE);
      if (matches && matches.length > 1) {
        issues.push({
          severity: 'warning',
          message: `This text has ${matches.length} "\${data.xxx}" placeholders in one string — Meta often only resolves the first one. Safer to split into separate ${comp.type} components, one placeholder each.`,
          path: `${path}.text`,
        });
      }
    }

    // Recurse into nested children (e.g. Form wraps inputs)
    if (Array.isArray(comp.children)) {
      walkComponents(comp.children, `${path}.children`, issues, root);
    }
  });
}

// ============================================================
//  UI
// ============================================================
const CATEGORY_META = {
  OTHER:               { icon: 'fa-shapes',              label: 'Other' },
  SIGN_UP:             { icon: 'fa-user-plus',           label: 'Sign up' },
  SIGN_IN:             { icon: 'fa-right-to-bracket',    label: 'Sign in' },
  APPOINTMENT_BOOKING: { icon: 'fa-calendar-check',      label: 'Appointment booking' },
  LEAD_GENERATION:     { icon: 'fa-bullseye',            label: 'Lead generation' },
  CONTACT_US:          { icon: 'fa-envelope',            label: 'Contact us' },
  CUSTOMER_SUPPORT:    { icon: 'fa-headset',             label: 'Customer support' },
  SURVEY:              { icon: 'fa-square-poll-vertical', label: 'Survey' },
  SHOPPING:            { icon: 'fa-bag-shopping',        label: 'Shopping' },
};
const catMeta = (c) => CATEGORY_META[c] ?? { icon: 'fa-shapes', label: String(c || 'Other').replace(/_/g, ' ').toLowerCase() };

function fmtShortDate(raw) {
  if (!raw) return '—';
  const d = new Date(String(raw).replace(' ', 'T'));
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function useBodyLock() {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);
}

function StatusBadge({ status }) {
  const s = STATUS_META[status] ?? STATUS_META.draft;
  return (
    <span className={`flx-status is-${s.tone}`}>
      <span className="flx-status-dot" />{s.label}
    </span>
  );
}

function Spinner({ light = false }) {
  return <span className={`flx-spin ${light ? 'is-light' : ''}`} />;
}

/** Shared sheet: centred dialog on desktop, bottom sheet on phones. */
function Sheet({ icon, tone = 'brand', title, sub, onClose, closable = true, wide, children, footer, className = '' }) {
  useBodyLock();
  useEffect(() => {
    if (!closable) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, closable]);
  return (
    <div className="flx-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && closable) onClose(); }}>
      <div className={`flx-sheet ${wide ? 'is-wide' : ''} ${className}`} role="dialog" aria-label={typeof title === 'string' ? title : undefined}>
        <span className="flx-grip" />
        <div className="flx-sheet-head">
          <span className={`flx-sheet-ic is-${tone}`}><i className={`fa-solid ${icon}`} /></span>
          <div className="flx-sheet-title"><h3>{title}</h3>{sub && <p>{sub}</p>}</div>
          {closable && <button type="button" className="flx-icon-btn" onClick={onClose} aria-label="Close"><i className="fa-solid fa-xmark" /></button>}
        </div>
        <div className="flx-sheet-body">{children}</div>
        {footer && <div className="flx-sheet-foot">{footer}</div>}
      </div>
    </div>
  );
}

function Note({ tone = 'info', icon, children, action }) {
  const ICON = { info: 'fa-circle-info', warn: 'fa-triangle-exclamation', danger: 'fa-circle-exclamation', ok: 'fa-circle-check' };
  return (
    <div className={`flx-note is-${tone}`}>
      <i className={`fa-solid ${icon ?? ICON[tone]}`} />
      <div className="flx-note-body">{children}</div>
      {action}
    </div>
  );
}

function CopyChip({ value, label }) {
  const [done, setDone] = useState(false);
  return (
    <button type="button" className={`flx-chip is-mono ${done ? 'is-done' : ''}`} title="Copy"
      onClick={(e) => { e.stopPropagation(); copyToClipboard(value); setDone(true); setTimeout(() => setDone(false), 1400); }}>
      {label ?? value}<i className={`fa-solid ${done ? 'fa-check' : 'fa-copy'}`} />
    </button>
  );
}

// ── Confirm Modal ─────────────────────────────────────────────
/**
 * Reusable confirm dialog — replaces all window.confirm() calls.
 *   variant – 'danger' | 'warning' | 'info'  (default 'danger')
 */
function ConfirmModal({ title, message, detail, confirmLabel = 'Confirm', variant = 'danger', onConfirm, onClose }) {
  const ICONS = { danger: 'fa-trash-can', warning: 'fa-triangle-exclamation', info: 'fa-circle-info' };
  const TONE  = { danger: 'danger', warning: 'warn', info: 'brand' };
  return (
    <Sheet icon={ICONS[variant] ?? ICONS.danger} tone={TONE[variant] ?? 'danger'} title={title} onClose={onClose} className="is-confirm"
      footer={<>
        <button type="button" className="flx-btn ghost" onClick={onClose}>Cancel</button>
        <button type="button" className={`flx-btn ${variant === 'danger' ? 'danger' : 'primary'}`} onClick={() => { onConfirm(); onClose(); }}>
          {confirmLabel}
        </button>
      </>}>
      {message && <p className="flx-muted">{message}</p>}
      {detail && <div className="flx-detail">{detail}</div>}
    </Sheet>
  );
}

// ── Create Flow Modal ─────────────────────────────────────────
function CreateFlowModal({ bId, hasWaba, isRental, defaultEndpoint, onClose, onCreated }) {
  const [form, setForm] = useState({ fName: '', fCategory: 'OTHER', fEndpointUri: isRental ? (defaultEndpoint || '') : '' });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function submit() {
    if (!form.fName.trim()) return toast.error('Flow name is required');
    if (!hasWaba) return toast.error('Set your WABA ID in Bot Settings first');
    setBusy(true);
    try {
      const data = await flowsApi(bId, { action: 'create', ...form });
      if (data.success) { toast.success('Flow created on Meta!'); onCreated(); onClose(); }
      else toast.error(data.message ?? 'Failed to create flow');
    } catch { toast.error('Network error'); }
    setBusy(false);
  }

  return (
    <Sheet icon="fa-wand-magic-sparkles" title="New WhatsApp Flow" sub="Created on Meta as a draft — you can edit it before publishing" onClose={onClose} wide
      footer={<>
        <button type="button" className="flx-btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" className="flx-btn primary" onClick={submit} disabled={busy || !hasWaba}>
          {busy ? <><Spinner light /> Creating…</> : <><i className="fa-solid fa-plus" /> Create flow</>}
        </button>
      </>}>
      {!hasWaba && (
        <Note tone="warn">No WABA ID set. Go to <b>Overview → Settings</b> and add it first.</Note>
      )}
      <label className="flx-field">
        <span className="flx-label">Flow name</span>
        <input className="flx-input" value={form.fName} placeholder="e.g. Customer registration"
          onChange={e => set('fName', e.target.value)} autoFocus onKeyDown={e => { if (e.key === 'Enter') submit(); }} />
      </label>
      <div className="flx-field">
        <span className="flx-label">Category</span>
        <div className="flx-cat-grid">
          {CATEGORIES.map(c => {
            const m = catMeta(c);
            return (
              <button key={c} type="button" className={`flx-cat ${form.fCategory === c ? 'is-on' : ''}`} onClick={() => set('fCategory', c)}>
                <i className={`fa-solid ${m.icon}`} /><span>{m.label}</span>
              </button>
            );
          })}
        </div>
      </div>
      <label className="flx-field">
        <span className="flx-label">Data endpoint URI <em>optional</em></span>
        <input className="flx-input mono" value={form.fEndpointUri} placeholder="https://yourserver.com/flow-endpoint"
          onChange={e => set('fEndpointUri', e.target.value)} />
        <span className="flx-hint">
          {isRental
            ? 'Pre-filled with this platform’s built-in endpoint, so the health check works without your own server. Replace it if you have one.'
            : 'Only needed for flows that load data from your server. Leave blank for static forms.'}
        </span>
      </label>
    </Sheet>
  );
}

// ── Upload Public Key Modal ───────────────────────────────────
function UploadKeyModal({ bId, onClose, onUploaded }) {
  const [pubKey, setPubKey] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!pubKey.trim()) return toast.error('Paste your public key first');
    setBusy(true);
    try {
      const data = await flowsApi(bId, { action: 'upload_public_key', public_key: pubKey.trim() });
      if (data.success) {
        toast.success('Public key uploaded to Meta!');
        onUploaded();
        onClose();
      } else {
        toast.error(data.message ?? 'Upload failed');
      }
    } catch { toast.error('Network error'); }
    setBusy(false);
  }

  return (
    <Sheet icon="fa-upload" title="Upload your own public key" sub="For people running their own server" onClose={onClose} wide
      footer={<>
        <button type="button" className="flx-btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" className="flx-btn primary" onClick={submit} disabled={busy || !pubKey.trim()}>
          {busy ? <><Spinner light /> Uploading…</> : <><i className="fa-solid fa-upload" /> Upload to Meta</>}
        </button>
      </>}>
      <Note tone="info">
        Generate an RSA 2048-bit key pair on your server, paste <b>public.pem</b> below, and keep <b>private.pem</b> on
        your server — your endpoint uses it to decrypt Flow data.
        <pre className="flx-pre">{'openssl genrsa -out private.pem 2048\nopenssl rsa -in private.pem -pubout -out public.pem'}</pre>
      </Note>
      <label className="flx-field">
        <span className="flx-label">Public key (PEM)</span>
        <textarea className="flx-input mono flx-key-area" value={pubKey} onChange={e => setPubKey(e.target.value)} rows={8}
          placeholder={'-----BEGIN PUBLIC KEY-----\nMIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKC...\n-----END PUBLIC KEY-----'} spellCheck={false} />
      </label>
    </Sheet>
  );
}

// ── Generate Key Pair Modal ──────────────────────────────────
// Two steps: confirm (extra warning if a key already exists), then a
// one-time reveal of the private key with copy/download — we never show
// it again after this, since the server only ever returns it once.
function GenerateKeyModal({ bId, hasExistingKey, onClose, onGenerated }) {
  const [step, setStep] = useState(hasExistingKey ? 'confirm-replace' : 'confirm');
  const [busy, setBusy] = useState(false);
  const [wantReveal, setWantReveal] = useState(false); // opt-in: also show the private key for self-hosting
  const [publicKey, setPublicKey] = useState('');
  const [privateKey, setPrivateKey] = useState(''); // only ever populated if wantReveal was checked
  const [savedConfirmed, setSavedConfirmed] = useState(false);

  async function generate(force = false) {
    setBusy(true);
    try {
      const data = await flowsApi(bId, { action: 'generate_key_pair', force, reveal: wantReveal });
      if (data.success) {
        setPublicKey(data.public_key || '');
        setPrivateKey(data.private_key || '');
        setStep(data.private_key ? 'reveal' : 'done');
        onGenerated();
      } else {
        toast.error(data.message ?? 'Could not generate key pair');
      }
    } catch { toast.error('Network error'); }
    setBusy(false);
  }

  function downloadPem(content, filename) {
    const blob = new Blob([content], { type: 'application/x-pem-file' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  const revealPending = step === 'reveal' && !savedConfirmed;
  const KeyBox = ({ label, value, file, secret }) => (
    <div className={`flx-keybox ${secret ? 'is-secret' : ''}`}>
      <div className="flx-keybox-head">
        <span><i className={`fa-solid ${secret ? 'fa-lock' : 'fa-key'}`} /> {label}</span>
        <span className="flx-keybox-actions">
          <button type="button" className="flx-btn ghost xs" onClick={() => { navigator.clipboard?.writeText(value); toast.success(`${secret ? 'Private' : 'Public'} key copied`); }}>
            <i className="fa-regular fa-copy" /> Copy
          </button>
          <button type="button" className="flx-btn ghost xs" onClick={() => downloadPem(value, file)}>
            <i className="fa-solid fa-download" /> {file}
          </button>
        </span>
      </div>
      <textarea readOnly className="flx-keybox-text" value={value} rows={secret ? 7 : 4} onClick={e => e.target.select()} spellCheck={false} />
    </div>
  );

  return (
    <Sheet
      icon={step === 'confirm-replace' ? 'fa-rotate' : step === 'confirm' ? 'fa-key' : 'fa-circle-check'}
      tone={step === 'confirm-replace' ? 'danger' : step === 'confirm' ? 'brand' : 'ok'}
      title={step === 'reveal' ? 'Your key pair is ready' : step === 'done' ? 'Encryption is set up' : step === 'confirm-replace' ? 'Replace encryption key?' : 'Set up encryption'}
      sub={step === 'confirm' ? 'One-time setup — no terminal or openssl needed' : null}
      onClose={onClose} closable={!revealPending} wide
      footer={<>
        {step === 'confirm' && <>
          <button type="button" className="flx-btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="flx-btn primary" onClick={() => generate(false)} disabled={busy}>
            {busy ? <><Spinner light /> Generating…</> : <><i className="fa-solid fa-wand-magic-sparkles" /> Generate key pair</>}
          </button>
        </>}
        {step === 'confirm-replace' && <>
          <button type="button" className="flx-btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="flx-btn danger" onClick={() => generate(true)} disabled={busy}>
            {busy ? <><Spinner light /> Generating…</> : <><i className="fa-solid fa-rotate" /> Replace existing key</>}
          </button>
        </>}
        {(step === 'reveal' || step === 'done') && (
          <button type="button" className="flx-btn primary" onClick={onClose} disabled={step === 'reveal' && !savedConfirmed}>Done</button>
        )}
      </>}>
      {(step === 'confirm' || step === 'confirm-replace') && (
        <>
          {step === 'confirm' ? (
            <>
              <div className="flx-how">
                {[
                  ['fa-key', 'We create an RSA 2048-bit key pair'],
                  ['fa-server', 'The private key is stored securely on this server'],
                  ['fa-cloud-arrow-up', 'The public key is uploaded to Meta for you'],
                ].map(([ic, t], i) => (
                  <div key={t} className="flx-how-step"><span>{i + 1}</span><i className={`fa-solid ${ic}`} /><b>{t}</b></div>
                ))}
              </div>
              <p className="flx-muted">Your bot has no key pair yet, so this is safe to run — it won’t affect anything that’s working now.</p>
            </>
          ) : (
            <Note tone="danger">
              <b>This bot already has a key pair.</b> Generating a new one replaces it. Until Meta confirms the new public key
              (which happens automatically here), Flow encryption for this bot could briefly stop working. Only do this if
              your key is lost, compromised, or you’re rotating it on purpose.
            </Note>
          )}
          <label className="flx-check">
            <input type="checkbox" checked={wantReveal} onChange={e => setWantReveal(e.target.checked)} />
            <span>
              <b>I run my own server for this.</b> Show me the private key once, right after generating, so I can copy it into
              my own endpoint.
            </span>
          </label>
        </>
      )}

      {step === 'reveal' && (
        <>
          <Note tone="ok">Both keys are ready and the public key is already on Meta. You asked to self-host, so you get both below.</Note>
          <KeyBox label="Public key — already uploaded to Meta" value={publicKey} file="public.pem" />
          <Note tone="danger" icon="fa-lock">
            <b>Save the private key now — it can’t be shown again after you close this.</b> It decrypts real customer data:
            keep it in a password manager or on your own server, never anywhere public.
          </Note>
          <KeyBox label="Private key — for your own endpoint" value={privateKey} file="private.pem" secret />
          <p className="flx-muted small">It’s also saved on this server either way — you only need your own copy for a separate endpoint.</p>
          <label className="flx-check is-strong">
            <input type="checkbox" checked={savedConfirmed} onChange={e => setSavedConfirmed(e.target.checked)} />
            <span>I’ve saved a copy of my private key somewhere safe.</span>
          </label>
        </>
      )}

      {step === 'done' && (
        <>
          <Note tone="ok"><b>Done — nothing more to do.</b> The private key is stored securely on this server and the public key is on Meta.</Note>
          {publicKey && (
            <>
              <KeyBox label="Public key sent to Meta" value={publicKey} file="public.pem" />
              <p className="flx-muted small">Shown so you can confirm what was sent — the public key isn’t sensitive. The private key is never shown here.</p>
            </>
          )}
        </>
      )}
    </Sheet>
  );
}

// ── Health Check Result Panel (mirrors Meta's UI) ─────────────
function HealthCheckResultPanel({ result, onRetry, checking }) {
  const [resultTab, setResultTab] = useState('actual'); // actual|expected|resolution

  if (!result && checking) {
    return (
      <div className="flx-hc is-checking">
        <div className="flx-hc-head">
          <span className="flx-hc-ic"><Spinner /></span>
          <div><b>Running health check…</b><span>Meta is sending an encrypted ping to your endpoint</span></div>
        </div>
      </div>
    );
  }
  if (!result) return null;

  const passed = result.passed;
  const actual = result.actual_result;
  const expected = result.expected_result;
  const resolution = result.resolution;

  return (
    <div className={`flx-hc ${passed ? 'is-pass' : 'is-fail'}`}>
      <div className="flx-hc-head">
        <span className="flx-hc-ic"><i className={`fa-solid ${passed ? 'fa-circle-check' : 'fa-plug-circle-xmark'}`} /></span>
        <div>
          <b>{passed ? 'Endpoint is active' : 'Endpoint is not active'}</b>
          <span>{passed ? 'Health check passed — Meta can reach and decrypt your endpoint' : 'Meta didn’t get the response it expected'}</span>
        </div>
        <button type="button" className={`flx-btn sm ${passed ? 'ghost' : 'dark'}`} onClick={onRetry} disabled={checking}>
          {checking ? <><Spinner /> Checking…</> : <><i className="fa-solid fa-rotate" /> Run again</>}
        </button>
      </div>

      {!passed && (
        <>
          <div className="flx-seg flx-hc-tabs">
            {[['actual', 'What happened'], ['expected', 'What Meta expects'], ['resolution', 'How to fix']].map(([k, l]) => (
              <button key={k} type="button" className={resultTab === k ? 'is-on' : ''} onClick={() => setResultTab(k)}>{l}</button>
            ))}
          </div>
          <div className="flx-hc-body">
            {resultTab === 'actual' && (
              <>
                <div className="flx-hc-pill"><i className="fa-solid fa-ban" /> Failed to receive expected HTTP response</div>
                {actual && (
                  <div className="flx-code">
                    {actual.status_code != null && <div>Status code: <b className="is-bad">{actual.status_code}</b></div>}
                    {actual.body && <div className="flx-code-body">Body: {actual.body}</div>}
                    {!actual.status_code && !actual.body && actual.error_msg && <div className="flx-code-body">{actual.error_msg}</div>}
                  </div>
                )}
              </>
            )}
            {resultTab === 'expected' && (
              <>
                <div className="flx-code">
                  <div>Status code: <b className="is-good">{expected?.status_code}</b></div>
                  <div className="flx-code-body">Body: {expected?.body}</div>
                </div>
                <p className="flx-muted">
                  Your endpoint must decrypt Meta’s ping with your RSA private key and return:
                </p>
                <pre className="flx-pre">{`{"data":{"status":"active","version":"v3.0"}}`}</pre>
              </>
            )}
            {resultTab === 'resolution' && <div className="flx-hc-fix">{resolution}</div>}
          </div>
        </>
      )}
    </div>
  );
}

// ── CodeMirror JSON editor (lazy) ─────────────────────────────
let _cmPromise = null;
function loadCodeMirror() {
  if (_cmPromise) return _cmPromise;
  _cmPromise = import(/* webpackChunkName: "codemirror" */ '../../../lib/codemirrorBundle')
    .then(m => m.default || m)
    .catch(err => { _cmPromise = null; throw err; });
  return _cmPromise;
}

/**
 * Flow JSON editor. CodeMirror when it loads (syntax colours, folding,
 * inline error squiggles from both the JSON parser and this tab's own
 * lint + Meta's errors); falls back to a plain textarea with line numbers.
 * `jumpTo` = { line, column, n } — moves the cursor there whenever n changes.
 */
function FlowJsonEditor({ value, onChange, readOnly, diagnostics, jumpTo, onCursor }) {
  const hostRef = useRef(null);
  const viewRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const onCursorRef = useRef(onCursor);
  const diagRef = useRef(diagnostics);
  const [mode, setMode] = useState('loading'); // loading | cm | plain
  const textRef = useRef(null);
  const gutterRef = useRef(null);

  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  useEffect(() => { onCursorRef.current = onCursor; }, [onCursor]);
  useEffect(() => { diagRef.current = diagnostics; }, [diagnostics]);

  useEffect(() => {
    let cancelled = false;
    let view = null;
    loadCodeMirror().then((cm) => {
      if (cancelled || !hostRef.current) return;
      const {
        EditorState, EditorView, keymap, lineNumbers, highlightActiveLineGutter, drawSelection,
        highlightActiveLine, highlightSpecialChars, defaultKeymap, history, historyKeymap, indentWithTab,
        defaultHighlightStyle, syntaxHighlighting, indentOnInput, bracketMatching, foldGutter, foldKeymap,
        closeBrackets, closeBracketsKeymap, lintKeymap, linter, json: jsonLang, jsonParseLinter, oneDark,
      } = cm;
      const parseLint = jsonParseLinter();
      const flowLint = linter((v) => {
        const doc = v.state.doc;
        const out = [...parseLint(v)];
        for (const d of diagRef.current ?? []) {
          if (!d.line || d.line > doc.lines) continue;
          const ln = doc.line(d.line);
          const from = Math.min(ln.to, ln.from + Math.max(0, (d.column ?? 1) - 1));
          out.push({ from, to: Math.max(from + 1, Math.min(ln.to, from + 1)), severity: d.severity === 'warning' ? 'warning' : 'error', message: d.message });
        }
        return out;
      }, { delay: 400 });

      const state = EditorState.create({
        doc: value,
        extensions: [
          lineNumbers(), foldGutter(), highlightActiveLineGutter(), highlightSpecialChars(), history(), drawSelection(),
          highlightActiveLine(), indentOnInput(), syntaxHighlighting(defaultHighlightStyle, { fallback: true }), bracketMatching(),
          closeBrackets(), jsonLang(), flowLint,
          keymap.of([...closeBracketsKeymap, ...defaultKeymap, ...historyKeymap, ...foldKeymap, ...lintKeymap, indentWithTab]),
          oneDark,
          EditorView.theme({
            '&': { height: '100%', fontSize: '13px', backgroundColor: '#0B1410' },
            '.cm-scroller': { fontFamily: "ui-monospace,'SF Mono','JetBrains Mono',Menlo,Consolas,monospace", lineHeight: '1.6' },
            '.cm-gutters': { backgroundColor: '#0B1410', borderRight: '1px solid #18261F', color: '#4B6358' },
            '.cm-activeLine': { backgroundColor: 'rgba(0,200,140,.06)' },
            '.cm-activeLineGutter': { backgroundColor: 'rgba(0,200,140,.1)', color: '#9BE3C5' },
          }),
          EditorState.readOnly.of(!!readOnly),
          EditorView.updateListener.of((u) => {
            if (u.docChanged) onChangeRef.current?.(u.state.doc.toString());
            if (u.docChanged || u.selectionSet) {
              const head = u.state.selection.main.head;
              const line = u.state.doc.lineAt(head);
              onCursorRef.current?.({ line: line.number, column: head - line.from + 1 });
            }
          }),
        ],
      });
      view = new EditorView({ state, parent: hostRef.current });
      viewRef.current = view;
      setMode('cm');
    }).catch(() => { if (!cancelled) setMode('plain'); });
    return () => { cancelled = true; if (view) view.destroy(); viewRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly]);

  // External changes (Format) → editor
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    const cur = view.state.doc.toString();
    if (cur !== value) view.dispatch({ changes: { from: 0, to: cur.length, insert: value } });
  }, [value]);

  // Re-run lint when diagnostics change (a no-op transaction nudges the linter)
  useEffect(() => {
    const view = viewRef.current;
    if (view) view.dispatch({});
  }, [diagnostics]);

  // Jump to an error location
  useEffect(() => {
    if (!jumpTo?.line) return;
    if (mode === 'cm' && viewRef.current) {
      const view = viewRef.current;
      const doc = view.state.doc;
      const ln = doc.line(Math.min(jumpTo.line, doc.lines));
      const pos = Math.min(ln.to, ln.from + Math.max(0, (jumpTo.column ?? 1) - 1));
      view.dispatch({ selection: { anchor: pos }, scrollIntoView: true });
      view.focus();
    } else if (mode === 'plain' && textRef.current) {
      const ta = textRef.current;
      const lines = value.split('\n');
      let offset = 0;
      for (let i = 0; i < jumpTo.line - 1 && i < lines.length; i++) offset += lines[i].length + 1;
      offset += Math.max(0, (jumpTo.column ?? 1) - 1);
      ta.focus();
      ta.selectionStart = ta.selectionEnd = Math.min(offset, value.length);
      const lh = parseFloat(window.getComputedStyle(ta).lineHeight) || 20;
      ta.scrollTop = Math.max(0, (jumpTo.line - 1) * lh - ta.clientHeight / 2);
      if (gutterRef.current) gutterRef.current.scrollTop = ta.scrollTop;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jumpTo?.n, mode]);

  function onKeyDown(e) {
    if (e.key === 'Tab') {
      e.preventDefault();
      const ta = textRef.current;
      const s = ta.selectionStart, en = ta.selectionEnd;
      const v = value.substring(0, s) + '  ' + value.substring(en);
      onChange(v);
      requestAnimationFrame(() => { ta.selectionStart = ta.selectionEnd = s + 2; });
    }
  }

  return (
    <div className="flx-code-host">
      <div ref={hostRef} className={`flx-cm ${mode === 'cm' ? '' : 'is-hidden'}`} />
      {mode === 'loading' && <div className="flx-code-loading"><Spinner light /> Loading editor…</div>}
      {mode === 'plain' && (
        <div className="flx-plain">
          <div className="flx-plain-gutter" ref={gutterRef}>
            {(value || '').split('\n').map((_, i) => <span key={i}>{i + 1}</span>)}
          </div>
          <textarea ref={textRef} className="flx-plain-text" value={value} onChange={e => onChange(e.target.value)} onKeyDown={onKeyDown}
            onScroll={e => { if (gutterRef.current) gutterRef.current.scrollTop = e.target.scrollTop; }}
            readOnly={readOnly} placeholder={`{\n  "version": "7.1",\n  "screens": [...]\n}`} spellCheck={false} />
        </div>
      )}
    </div>
  );
}

// ── Flow Editor (full-screen overlay) ────────────────────────
function FlowEditor({ flow, bId, bot, isRental, defaultEndpoint, onClose, onSaved }) {
  const [name,           setName]          = useState(flow.fName        ?? '');
  const [category,       setCategory]      = useState(flow.fCategory    ?? 'OTHER');
  const [endpoint,       setEndpoint]      = useState(flow.fEndpointUri || (isRental ? (defaultEndpoint || '') : ''));
  const [jsonDef,        setJsonDef]       = useState(flow.fJsonDef     ?? '');
  const [preview,        setPreview]       = useState(null);
  const [previewErr,     setPreviewErr]    = useState('');
  const [saving,         setSaving]        = useState(false);
  const [publishing,     setPublishing]    = useState(false);

  // Health check state
  const [healthChecking, setHealthChecking] = useState(false);
  const [healthResult,   setHealthResult]   = useState(null); // null = not run yet

  const [showLogs,       setShowLogs]       = useState(false);

  // Encryption / public key state
  const [encStatus,      setEncStatus]     = useState(null);  // null=not loaded, object=loaded
  const [encLoading,     setEncLoading]    = useState(false);
  const [showKeyModal,   setShowKeyModal]  = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [keyMarkedDone,  setKeyMarkedDone] = useState(false);
  const [confirmPublish, setConfirmPublish] = useState(false);

  const [validating,     setValidating]    = useState(false);
  const [metaErrors,     setMetaErrors]    = useState(null);
  const [lintErrors,     setLintErrors]    = useState([]);
  const [, setHealthStatus]  = useState(null);
  const [activeStep,     setActiveStep]    = useState(null);
  const [tab,            setTab]           = useState('editor');
  const [mobilePane,     setMobilePane]    = useState('code'); // code | preview (phones only)
  const [jumpTarget,     setJumpTarget]    = useState(null);
  const [cursor,         setCursor]        = useState({ line: 1, column: 1 });
  const [savedJson,      setSavedJson]     = useState(flow.fJsonDef ?? '');
  const [confirmLeave,   setConfirmLeave]  = useState(false);
  const validateTimer = useRef(null);

  // Parse JSON for live preview + run local schema lint
  useEffect(() => {
    if (!jsonDef.trim()) { setPreview(null); setPreviewErr(''); setLintErrors([]); return; }
    try {
      const parsed = JSON.parse(jsonDef);
      setPreview(parsed);
      setPreviewErr('');
      const issues = lintFlowJson(parsed);
      // Attach real [Line, Column] to every issue, computed locally.
      try {
        const { positions } = parseJsonWithPositions(jsonDef);
        issues.forEach(issue => {
          const p = resolvePathPosition(positions, issue.path, issue.preferValue);
          if (p) { issue.line = p.line; issue.column = p.column; }
        });
      } catch { /* position parse failed -- issues still show without a location */ }
      setLintErrors(issues);
    } catch (e) {
      setPreview(null);
      setPreviewErr(e.message);
      setLintErrors([]);
    }
  }, [jsonDef]);

  // Auto-revalidate with Meta 3s after the user stops typing
  useEffect(() => {
    if (!flow.fMetaFlowId || previewErr || !jsonDef.trim()) return;
    clearTimeout(validateTimer.current);
    validateTimer.current = setTimeout(() => { fetchValidation(); }, 3000);
    return () => clearTimeout(validateTimer.current);
  }, [jsonDef]); // eslint-disable-line

  // On open: fetch validation + encryption status
  useEffect(() => {
    if (flow.fMetaFlowId) fetchValidation();
    fetchEncryptionStatus();
  }, []); // eslint-disable-line

  async function fetchValidation() {
    setValidating(true);
    try {
      const data = await flowsApi(bId, { action: 'validate', fId: flow.fId });
      if (data.success) {
        setMetaErrors(data.validation_errors ?? []);
        setHealthStatus(data.health_status ?? null);
      }
    } catch {}
    setValidating(false);
  }

  async function fetchEncryptionStatus() {
    setEncLoading(true);
    try {
      const data = await flowsApi(bId, { action: 'get_encryption_status' });
      if (data.success) {
        // has_key = existence, is_healthy = works (see isEncryptionKeyHealthy)
        setEncStatus({ ...data, is_healthy: isEncryptionKeyHealthy(data) });
        if (data.key_marked_done) setKeyMarkedDone(true);
      } else {
        setEncStatus({ has_key: false, key_status: 'unknown' });
      }
    } catch {
      setEncStatus({ has_key: false, key_status: 'unknown' });
    }
    setEncLoading(false);
  }

  // Switches to the editor and puts the cursor on the error.
  function jumpToLine(line, column) {
    setTab('editor');
    setMobilePane('code');
    if (line) setJumpTarget(j => ({ line, column, n: (j?.n ?? 0) + 1 }));
  }

  function formatJson() {
    try   { setJsonDef(JSON.stringify(JSON.parse(jsonDef), null, 2)); toast.success('JSON formatted'); }
    catch { toast.error('Invalid JSON — fix errors first'); }
  }

  async function save() {
    if (!name.trim()) return toast.error('Flow name required');
    setSaving(true);
    try {
      const data = await flowsApi(bId, {
        action: 'update_json', fId: flow.fId,
        fName: name, fCategory: category, fEndpointUri: endpoint, fJsonDef: jsonDef,
      });
      if (data.success) {
        toast.success('Flow saved — re-validating…');
        setSavedJson(jsonDef);
        await fetchValidation();
        const newStatus = flow.fStatus === 'published' ? 'draft' : flow.fStatus;
        onSaved({ ...flow, fName: name, fCategory: category, fEndpointUri: endpoint, fJsonDef: jsonDef, fStatus: newStatus });
      } else toast.error(data.message ?? 'Save failed');
    } catch { toast.error('Network error'); }
    setSaving(false);
  }

  async function publish() {
    if (!flow.fMetaFlowId) return toast.error('Flow not yet linked to Meta');
    if (metaErrors && metaErrors.length > 0) return toast.error('Fix all JSON errors before publishing');
    setConfirmPublish(true);
  }

  async function doPublish() {
    setPublishing(true);
    try {
      await flowsApi(bId, {
        action: 'update_json', fId: flow.fId,
        fName: name, fCategory: category, fEndpointUri: endpoint, fJsonDef: jsonDef,
      });
      const data = await flowsApi(bId, { action: 'publish', fId: flow.fId });
      if (data.success) { toast.success('Flow published! 🚀'); onClose(); }
      else toast.error(data.message ?? 'Publish failed');
    } catch { toast.error('Network error'); }
    setPublishing(false);
  }

  // ── Real health check — calls Meta API ──
  async function runHealthCheck() {
    if (!flow.fMetaFlowId) return toast.error('Flow is not linked to Meta yet');
    setHealthChecking(true);
    setHealthResult(null);
    try {
      const data = await flowsApi(bId, { action: 'health_check', fId: flow.fId, fEndpointUri: endpoint });
      setHealthResult(data);
      if (data.passed) toast.success('Health check passed ✓');
      else toast.error('Health check failed — see details');
    } catch {
      toast.error('Network error during health check');
      setHealthResult({ passed: false, actual_result: { error_msg: 'Network error' }, resolution: 'Could not reach the server. Check your connection.' });
    }
    setHealthChecking(false);
  }

  async function markKeyDone() {
    setKeyMarkedDone(true);
    setActiveStep(null);
    toast.success('Public key marked as done');
    try {
      const res = await flowsApi(bId, { action: 'mark_key_done', done: true });
      if (!res.success) toast.error('Could not save — will reset on reload');
    } catch { toast.error('Network error — will reset on reload'); }
  }

  const isDeprecated = flow.fStatus === 'deprecated';
  const canEdit      = !isDeprecated;
  const dirty        = jsonDef !== savedJson || name !== (flow.fName ?? '') || category !== (flow.fCategory ?? 'OTHER')
                       || endpoint !== (flow.fEndpointUri || (isRental ? (defaultEndpoint || '') : ''));

  const localJsonErr = previewErr && jsonDef.trim() ? 1 : 0;
  const metaErrCount = metaErrors ? metaErrors.length : 0;
  // Warnings flag a known Meta quirk and don't block Publish.
  const lintBlocking = lintErrors.filter(e => e.severity !== 'warning');
  const lintWarnings = lintErrors.filter(e => e.severity === 'warning');
  const lintErrCount = lintBlocking.length;
  const totalErrors  = (metaErrors !== null ? metaErrCount : localJsonErr) + lintErrCount;
  const hasErrors    = totalErrors > 0;

  const endpointIsSet = !!endpoint.trim();
  const screenCount   = preview?.screens?.length ?? 0;
  // is_healthy, not has_key: has_key only means "something exists", not "it works".
  const keyIsDone = keyMarkedDone || !!encStatus?.is_healthy;

  // Inline editor squiggles: our lint + Meta's located errors
  const diagnostics = React.useMemo(() => [
    ...lintErrors.filter(e => e.line).map(e => ({ line: e.line, column: e.column, message: e.message, severity: e.severity })),
    ...(metaErrors ?? []).filter(e => e.line_start).map(e => ({ line: e.line_start, column: e.column_start, message: e.error_message ?? e.message ?? 'Meta error', severity: 'error' })),
  ], [lintErrors, metaErrors]);

  // ── Setup steps — "done" is driven by real data ──
  const STEP_HELP = [
    {
      label: 'Set endpoint URI',
      icon: 'fa-link',
      done:  endpointIsSet,
      help:  endpointIsSet
        ? 'This identifies the endpoint the Flow exchanges data with.'
        : 'Enter the URL of the server that handles WhatsApp Flow data exchange. It must accept POST requests with an encrypted body from Meta.',
      action: { fn: 'edit_endpoint' },
    },
    {
      label: 'Add phone number',
      icon: 'fa-phone',
      done:  !!flow.fMetaFlowId,
      help:  flow.fMetaFlowId
        ? 'A phone number is already linked to your WhatsApp Business Account.'
        : 'Your WhatsApp Business phone number must be linked to this flow. This happens automatically when you create a flow here with your connected WABA.',
      action: flow.fMetaFlowId ? { label: 'Manage phone numbers', url: 'https://business.facebook.com/wa/manage/phone-numbers/' } : null,
    },
    {
      label: 'Sign public key',
      icon: 'fa-key',
      done:  encLoading ? false : keyIsDone,
      loading: encLoading,
      help:  encLoading
        ? 'Checking encryption status…'
        : keyIsDone
          ? `A public key has been signed.${encStatus?.key_fingerprint ? `\n\nFingerprint: ${encStatus.key_fingerprint}` : ''}${encStatus?.key_uploaded_at ? `\nUploaded: ${new Date(encStatus.key_uploaded_at * 1000).toLocaleDateString()}` : ''}`
          : 'Meta encrypts flow data with your public key so only your server can read it. Generate a key pair here — it takes a second.',
      action: encLoading ? null : keyIsDone ? { label: 'Replace key', fn: 'upload_key' } : { label: 'Upload my own key', fn: 'upload_key' },
      secondaryAction: (!encLoading && !keyIsDone) ? { label: 'Key is already uploaded — mark as done', fn: 'mark_key_done' } : null,
    },
    {
      label: 'Connect Meta app',
      icon: 'fa-plug',
      done:  !!flow.fMetaFlowId,
      help:  flow.fMetaFlowId
        ? 'A Meta app lets the Flow talk to your endpoint. Once submitted, it can’t be changed.'
        : 'Your Meta App must be connected with whatsapp_business_messaging and whatsapp_business_management permissions. Set this in Bot Settings → App Secret.',
      metaAppName: flow.fMetaFlowId ? (bot?.bName ?? null) : null,
      action: null,
    },
    {
      label: 'Health check',
      icon: 'fa-heart-pulse',
      done:  healthResult?.passed === true,
      active: healthChecking,
      help:  healthResult?.passed
        ? 'Health check passed. Meta can reach and decrypt your endpoint.'
        : endpointIsSet
          ? 'Check that Meta can reach your endpoint. Meta sends an encrypted ping — your server must decrypt it and reply {"data":{"status":"active","version":"v3.0"}}.'
          : 'Set your endpoint URI first (step 1), then run the health check.',
      action: endpointIsSet && flow.fMetaFlowId ? { label: 'Run health check', fn: 'health' } : null,
    },
  ];
  const stepsDone = STEP_HELP.filter(s => s.done).length;

  const errorsTabBadge = validating ? '…' : totalErrors;
  const TABS = [
    { k: 'editor',   icon: 'fa-code',          label: 'Editor' },
    { k: 'errors',   icon: hasErrors ? 'fa-circle-exclamation' : 'fa-circle-check', label: 'Errors', badge: errorsTabBadge, tone: hasErrors ? 'bad' : 'ok' },
    { k: 'endpoint', icon: 'fa-plug',          label: 'Endpoint', badge: `${stepsDone}/${STEP_HELP.length}`, tone: stepsDone === STEP_HELP.length ? 'ok' : 'muted' },
    { k: 'actions',  icon: 'fa-rocket',        label: 'Publish' },
  ];

  const ErrRow = ({ err, warn, onClick, path, line, column }) => (
    <button type="button" className={`flx-err ${warn ? 'is-warn' : ''} ${onClick ? '' : 'is-static'}`} onClick={onClick} disabled={!onClick}>
      <i className={`fa-solid ${warn ? 'fa-triangle-exclamation' : 'fa-circle-xmark'}`} />
      <span className="flx-err-main">
        <b>{err}</b>
        {(path || line) && (
          <span className="flx-err-meta">
            {path && <code>{path}</code>}
            {line && <em>Line {line}{column ? `, col ${column}` : ''}</em>}
          </span>
        )}
      </span>
      {onClick && <i className="fa-solid fa-arrow-right flx-err-go" />}
    </button>
  );

  const step = activeStep !== null ? STEP_HELP[activeStep] : null;

  return (
    <div className="flx-ed">
      {/* ── Top bar ── */}
      <header className="flx-ed-top">
        <button type="button" className="flx-icon-btn" onClick={() => (dirty && canEdit ? setConfirmLeave(true) : onClose())} aria-label="Back to flows">
          <i className="fa-solid fa-arrow-left" />
        </button>
        <span className="flx-ed-cat"><i className={`fa-solid ${catMeta(category).icon}`} /></span>
        <div className="flx-ed-name">
          {canEdit
            ? <input value={name} onChange={e => setName(e.target.value)} placeholder="Flow name" aria-label="Flow name" />
            : <b>{name}</b>}
          <div className="flx-ed-sub">
            <StatusBadge status={flow.fStatus ?? 'draft'} />
            {dirty && canEdit && <span className="flx-unsaved"><span /> Unsaved</span>}
            <span className="flx-ed-upd">Updated {fmtShortDate(flow.fUpdatedAt)}</span>
            {flow.fMetaFlowId && <CopyChip value={flow.fMetaFlowId} label={`ID ${flow.fMetaFlowId}`} />}
          </div>
        </div>
        <div className="flx-ed-actions">
          {canEdit && (
            <>
              <button type="button" className={`flx-errpill ${hasErrors ? 'is-bad' : 'is-ok'}`} onClick={() => setTab('errors')} title="Validation">
                {validating ? <Spinner /> : <i className={`fa-solid ${hasErrors ? 'fa-circle-exclamation' : 'fa-circle-check'}`} />}
                <span>{validating ? 'Checking' : hasErrors ? `${totalErrors} error${totalErrors !== 1 ? 's' : ''}` : 'Valid'}</span>
              </button>
              <button type="button" className="flx-btn ghost" onClick={save} disabled={saving}>
                {saving ? <Spinner /> : <i className="fa-regular fa-floppy-disk" />}<span className="flx-hide-sm">{saving ? 'Saving…' : 'Save'}</span>
              </button>
              {flow.fMetaFlowId && (
                <button type="button" className="flx-btn primary" onClick={publish} disabled={publishing || hasErrors}
                  title={hasErrors ? 'Fix all JSON errors before publishing' : ''}>
                  {publishing ? <Spinner light /> : <i className="fa-solid fa-rocket" />}<span className="flx-hide-sm">{publishing ? 'Publishing…' : 'Publish'}</span>
                </button>
              )}
            </>
          )}
        </div>
      </header>

      {isDeprecated && (
        <div className="flx-ed-ribbon"><i className="fa-solid fa-box-archive" /> This flow is deprecated and read-only.</div>
      )}

      {/* ── Tabs ── */}
      <nav className="flx-ed-tabs">
        {TABS.map(t => (
          <button key={t.k} type="button" className={`flx-ed-tab ${tab === t.k ? 'is-on' : ''}`} onClick={() => setTab(t.k)}>
            <i className={`fa-solid ${t.icon} ${t.tone ? `is-${t.tone}` : ''}`} />{t.label}
            {t.badge !== undefined && <span className={`flx-tab-badge is-${t.tone ?? 'muted'}`}>{t.badge}</span>}
          </button>
        ))}
        <div className="flx-ed-tabs-right">
          <label className="flx-cat-select">
            <i className={`fa-solid ${catMeta(category).icon}`} />
            {canEdit ? (
              <select value={category} onChange={e => setCategory(e.target.value)} aria-label="Category">
                {CATEGORIES.map(c => <option key={c} value={c}>{catMeta(c).label}</option>)}
              </select>
            ) : <span>{catMeta(category).label}</span>}
          </label>
        </div>
      </nav>

      {/* ── Editor ── */}
      {tab === 'editor' && (
        <div className={`flx-ed-split show-${mobilePane}`}>
          <div className="flx-seg flx-ed-panepick">
            <button type="button" className={mobilePane === 'code' ? 'is-on' : ''} onClick={() => setMobilePane('code')}><i className="fa-solid fa-code" /> Code</button>
            <button type="button" className={mobilePane === 'preview' ? 'is-on' : ''} onClick={() => setMobilePane('preview')}><i className="fa-solid fa-mobile-screen" /> Preview{screenCount ? ` · ${screenCount}` : ''}</button>
          </div>
          <section className="flx-ed-code">
            <div className="flx-pane-head is-dark">
              <span className="flx-file"><i className="fa-solid fa-file-code" /> flow.json</span>
              {canEdit && (
                <button type="button" className="flx-btn on-dark xs" onClick={formatJson}><i className="fa-solid fa-align-left" /> Format</button>
              )}
            </div>
            <FlowJsonEditor value={jsonDef} onChange={setJsonDef} readOnly={!canEdit} diagnostics={diagnostics} jumpTo={jumpTarget} onCursor={setCursor} />
            <div className="flx-statusbar">
              {previewErr && jsonDef.trim()
                ? <span className="is-bad" title={previewErr}><i className="fa-solid fa-circle-xmark" /> {previewErr}</span>
                : <span className="is-ok"><i className="fa-solid fa-circle-check" /> Valid JSON</span>}
              {lintErrCount > 0 && <button type="button" className="is-bad" onClick={() => setTab('errors')}>{lintErrCount} schema issue{lintErrCount !== 1 ? 's' : ''}</button>}
              <span className="flx-statusbar-r">Ln {cursor.line}, Col {cursor.column} · {jsonDef ? jsonDef.split('\n').length : 0} lines</span>
            </div>
          </section>
          <section className="flx-ed-prev">
            <div className="flx-pane-head">
              <span className="flx-file"><i className="fa-solid fa-mobile-screen" /> Preview</span>
              {screenCount > 0 && <span className="flx-muted small">{screenCount} screen{screenCount !== 1 ? 's' : ''} · tap buttons to navigate</span>}
            </div>
            <ErrorBoundary key={jsonDef}>
              <FlowPreview screens={preview?.screens} />
            </ErrorBoundary>
          </section>
        </div>
      )}

      {/* ── Errors ── */}
      {tab === 'errors' && (
        <div className="flx-ed-panel">
          <div className="flx-panel-inner">
            <div className={`flx-verdict ${validating ? 'is-loading' : hasErrors ? 'is-bad' : 'is-ok'}`}>
              <span className="flx-verdict-ic">{validating ? <Spinner /> : <i className={`fa-solid ${hasErrors ? 'fa-circle-exclamation' : 'fa-circle-check'}`} />}</span>
              <div>
                <b>{validating ? 'Checking with Meta…' : hasErrors ? `${totalErrors} problem${totalErrors !== 1 ? 's' : ''} to fix before publishing` : 'No JSON errors'}</b>
                <span>{validating ? 'Fetching validation errors from Meta' : hasErrors ? 'Click any problem to jump to it in the editor.'
                  : flow.fMetaFlowId ? 'Meta and the local check both passed.' : 'Local check passed. Meta validation runs once the flow is linked to Meta.'}</span>
              </div>
              <div className="flx-verdict-actions">
                {flow.fMetaFlowId && (
                  <button type="button" className="flx-btn ghost sm" onClick={fetchValidation} disabled={validating}><i className="fa-solid fa-rotate" /> Re-check</button>
                )}
                {!validating && totalErrors > 0 && (
                  <button type="button" className="flx-btn ghost sm" onClick={() => { copyToClipboard(buildErrorsText({ lintBlocking, lintWarnings, metaErrors })); toast.success('Errors copied to clipboard'); }}>
                    <i className="fa-regular fa-copy" /> Copy all
                  </button>
                )}
              </div>
            </div>

            {!validating && metaErrors === null && localJsonErr > 0 && (
              <div className="flx-err-group">
                <h4>JSON can’t be read</h4>
                <ErrRow err="JSON parse error" path={previewErr} onClick={() => jumpToLine(null)} />
              </div>
            )}
            {!validating && lintBlocking.length > 0 && (
              <div className="flx-err-group">
                <h4>Local schema check <span>{lintBlocking.length}</span></h4>
                {lintBlocking.map((err, i) => (
                  <ErrRow key={`lint-${i}`} err={err.message} path={err.path} line={err.line} column={err.column} onClick={() => jumpToLine(err.line, err.column)} />
                ))}
              </div>
            )}
            {!validating && metaErrors && metaErrors.length > 0 && (
              <div className="flx-err-group">
                <h4>From Meta <span>{metaErrors.length}</span></h4>
                {metaErrors.map((err, i) => (
                  <ErrRow key={i} err={err.error_message ?? err.message ?? 'Unknown error'} path={err.path} line={err.line_start} column={err.column_start}
                    onClick={err.line_start ? () => jumpToLine(err.line_start, err.column_start) : undefined} />
                ))}
              </div>
            )}
            {!validating && lintWarnings.length > 0 && (
              <div className="flx-err-group">
                <h4 className="is-warn">Worth checking <span>{lintWarnings.length}</span></h4>
                {lintWarnings.map((err, i) => (
                  <ErrRow key={`warn-${i}`} warn err={err.message} path={err.path} line={err.line} column={err.column} onClick={() => jumpToLine(err.line, err.column)} />
                ))}
              </div>
            )}
            {!validating && metaErrors === null && !flow.fMetaFlowId && lintErrors.length === 0 && !localJsonErr && (
              <Note tone="info">This flow isn’t linked to Meta yet — create it on Meta to get full validation.</Note>
            )}
          </div>
        </div>
      )}

      {/* ── Publish ── */}
      {tab === 'actions' && (
        <div className="flx-ed-panel">
          <div className="flx-panel-inner narrow">
            <div className="flx-publish">
              <span className="flx-publish-art"><i className="fa-solid fa-rocket" /></span>
              <h3>{flow.fStatus === 'published' ? 'This flow is live' : isDeprecated ? 'This flow is deprecated' : 'Ready to go live?'}</h3>
              <p className="flx-muted">Publishing sends the latest JSON to Meta. Published flows can’t be edited — editing creates a new draft.</p>
              <ul className="flx-checklist">
                <li className={hasErrors ? 'is-bad' : 'is-ok'}>
                  <i className={`fa-solid ${hasErrors ? 'fa-circle-xmark' : 'fa-circle-check'}`} />
                  <span>{hasErrors ? `${totalErrors} JSON error${totalErrors !== 1 ? 's' : ''} to fix` : 'No JSON errors'}</span>
                  {hasErrors && <button type="button" className="flx-link" onClick={() => setTab('errors')}>Fix</button>}
                </li>
                <li className={flow.fMetaFlowId ? 'is-ok' : 'is-bad'}>
                  <i className={`fa-solid ${flow.fMetaFlowId ? 'fa-circle-check' : 'fa-circle-xmark'}`} />
                  <span>{flow.fMetaFlowId ? 'Linked to Meta' : 'Not linked to Meta yet'}</span>
                </li>
                <li className={screenCount > 0 ? 'is-ok' : 'is-muted'}>
                  <i className={`fa-solid ${screenCount > 0 ? 'fa-circle-check' : 'fa-circle'}`} />
                  <span>{screenCount > 0 ? `${screenCount} screen${screenCount !== 1 ? 's' : ''}` : 'No screens yet'}</span>
                </li>
                {endpointIsSet && (
                  <li className={healthResult?.passed ? 'is-ok' : 'is-muted'}>
                    <i className={`fa-solid ${healthResult?.passed ? 'fa-circle-check' : 'fa-circle'}`} />
                    <span>{healthResult?.passed ? 'Endpoint health check passed' : 'Endpoint not checked this session'}</span>
                    {!healthResult?.passed && <button type="button" className="flx-link" onClick={() => setTab('endpoint')}>Check</button>}
                  </li>
                )}
              </ul>
              {canEdit && (
                <div className="flx-publish-actions">
                  <button type="button" className="flx-btn ghost" onClick={save} disabled={saving}>
                    {saving ? <Spinner /> : <i className="fa-regular fa-floppy-disk" />} Save draft
                  </button>
                  {flow.fMetaFlowId && (
                    <button type="button" className="flx-btn primary" onClick={publish} disabled={publishing || hasErrors} title={hasErrors ? 'Fix all errors first' : ''}>
                      {publishing ? <Spinner light /> : <i className="fa-solid fa-rocket" />} Publish
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Endpoint ── */}
      {tab === 'endpoint' && (
        <div className="flx-ed-panel">
          <div className="flx-panel-inner">
            {(healthResult || healthChecking) && (
              <HealthCheckResultPanel result={healthResult} onRetry={runHealthCheck} checking={healthChecking} />
            )}

            <div className="flx-ep-grid">
              <div className="flx-card">
                <div className="flx-card-head">
                  <div className="flx-ring" style={{ '--p': Math.round((stepsDone / STEP_HELP.length) * 100) }}><span>{stepsDone}/{STEP_HELP.length}</span></div>
                  <div><h4>Endpoint setup</h4><p className="flx-muted small">{stepsDone === STEP_HELP.length ? 'Everything is in place.' : 'Tap a step to see what to do.'}</p></div>
                </div>
                <ol className="flx-steps">
                  {STEP_HELP.map((s, i) => (
                    <li key={s.label}>
                      <button type="button" className={`flx-step ${s.done ? 'is-done' : s.active ? 'is-active' : ''} ${activeStep === i ? 'is-open' : ''}`}
                        onClick={() => setActiveStep(activeStep === i ? null : i)}>
                        <span className="flx-step-n">{s.loading || s.active ? <Spinner /> : s.done ? <i className="fa-solid fa-check" /> : i + 1}</span>
                        <span className="flx-step-txt"><b>{s.label}</b><em>{s.done ? 'Done' : s.loading ? 'Checking…' : 'To do'}</em></span>
                        <i className={`fa-solid fa-chevron-${activeStep === i ? 'up' : 'right'}`} />
                      </button>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="flx-card">
                {step ? (
                  <div className="flx-stephelp">
                    <div className="flx-stephelp-head">
                      <span className={`flx-sheet-ic ${step.done ? 'is-ok' : 'is-brand'}`}><i className={`fa-solid ${step.icon}`} /></span>
                      <h4>{step.label}</h4>
                      <button type="button" className="flx-icon-btn" onClick={() => setActiveStep(null)} aria-label="Close"><i className="fa-solid fa-xmark" /></button>
                    </div>
                    <p className="flx-stephelp-body">{step.help}</p>
                    {step.metaAppName && <div className="flx-detail"><i className="fa-brands fa-meta" /> {step.metaAppName}</div>}
                    {step.action && (
                      step.action.fn === 'edit_endpoint' ? (
                        <div className="flx-inline-form">
                          <input className="flx-input mono" value={endpoint} onChange={e => setEndpoint(e.target.value)} placeholder="https://yourserver.com/api/flow-data" disabled={!canEdit} />
                          {canEdit && (
                            <button type="button" className="flx-btn primary" onClick={() => { save(); setActiveStep(null); }} disabled={saving}>
                              {saving ? <Spinner light /> : <i className="fa-solid fa-check" />} Save
                            </button>
                          )}
                        </div>
                      ) : step.action.fn === 'health' ? (
                        <button type="button" className="flx-btn primary" onClick={() => { setActiveStep(null); runHealthCheck(); }} disabled={healthChecking}>
                          {healthChecking ? <Spinner light /> : <i className="fa-solid fa-heart-pulse" />} Run health check
                        </button>
                      ) : step.action.fn === 'upload_key' ? (
                        <div className="flx-stack">
                          <button type="button" className={`flx-btn ${keyIsDone ? 'ghost' : 'primary'}`} onClick={() => { setActiveStep(null); setShowGenerateModal(true); }}>
                            <i className="fa-solid fa-wand-magic-sparkles" /> {keyIsDone ? 'Generate a new key pair' : 'Generate key pair for me'}{!keyIsDone && <em className="flx-rec">Recommended</em>}
                          </button>
                          <button type="button" className="flx-btn ghost" onClick={() => { setActiveStep(null); setShowKeyModal(true); }}>
                            <i className="fa-solid fa-upload" /> {step.action.label}
                          </button>
                        </div>
                      ) : (
                        <a className="flx-btn ghost" href={step.action.url} target="_blank" rel="noopener noreferrer">
                          {step.action.label} <i className="fa-solid fa-arrow-up-right-from-square" />
                        </a>
                      )
                    )}
                    {step.secondaryAction?.fn === 'mark_key_done' && (
                      <button type="button" className="flx-link muted" onClick={markKeyDone}><i className="fa-solid fa-check" /> {step.secondaryAction.label}</button>
                    )}
                  </div>
                ) : (
                  <div className="flx-stephelp">
                    <div className="flx-stephelp-head">
                      <span className={`flx-sheet-ic ${endpointIsSet ? 'is-ok' : 'is-muted'}`}><i className="fa-solid fa-link" /></span>
                      <h4>{endpointIsSet ? 'Endpoint is set' : 'No endpoint yet'}</h4>
                    </div>
                    <p className="flx-stephelp-body">
                      {endpointIsSet ? 'Save any changes, then run the health check so Meta can confirm it works.' : 'Add your endpoint URL, or leave it blank for a static flow that doesn’t load data.'}
                    </p>
                    <label className="flx-field">
                      <span className="flx-label">Endpoint URI</span>
                      {canEdit
                        ? <input className="flx-input mono" value={endpoint} onChange={e => setEndpoint(e.target.value)} placeholder="https://yourserver.com/api/flow-data" />
                        : <div className="flx-detail">{endpoint || '(none)'}</div>}
                      {isRental && canEdit && <span className="flx-hint">Pre-filled with this platform’s built-in endpoint for rented numbers — replace it if you have your own server.</span>}
                    </label>
                    {canEdit && (
                      <div className="flx-row">
                        <button type="button" className="flx-btn ghost" onClick={save} disabled={saving}>{saving ? <Spinner /> : <i className="fa-regular fa-floppy-disk" />} Save</button>
                        {endpointIsSet && flow.fMetaFlowId && (
                          <button type="button" className="flx-btn primary" onClick={runHealthCheck} disabled={healthChecking}>
                            {healthChecking ? <Spinner light /> : <i className="fa-solid fa-heart-pulse" />} Run health check
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <button type="button" className="flx-logs-cta" onClick={() => setShowLogs(true)}>
              <span className="flx-sheet-ic is-dark"><i className="fa-solid fa-wave-square" /></span>
              <span><b>Traffic logs</b><em>Every data_exchange call from Meta — decrypted request, response, routing and errors.</em></span>
              <i className="fa-solid fa-chevron-right" />
            </button>
          </div>
        </div>
      )}

      {showKeyModal && (
        <UploadKeyModal bId={bId} onClose={() => setShowKeyModal(false)} onUploaded={() => { fetchEncryptionStatus(); }} />
      )}
      {showGenerateModal && (
        <GenerateKeyModal bId={bId} hasExistingKey={!!encStatus?.has_key || !!encStatus?.local_key_exists}
          onClose={() => setShowGenerateModal(false)} onGenerated={() => { fetchEncryptionStatus(); }} />
      )}
      {confirmPublish && (
        <ConfirmModal title="Publish this flow?" message="Once published, this flow can’t be edited. Changes go live immediately."
          detail={name} confirmLabel="Publish" variant="warning" onConfirm={doPublish} onClose={() => setConfirmPublish(false)} />
      )}
      {confirmLeave && (
        <ConfirmModal title="Leave without saving?" message="Your changes to this flow haven’t been saved." confirmLabel="Discard changes"
          variant="danger" onConfirm={onClose} onClose={() => setConfirmLeave(false)} />
      )}
      {showLogs && (
        <FlowLogsModal bId={bId} fId={flow.fId} flowName={name} onClose={() => setShowLogs(false)} />
      )}
    </div>
  );
}

// ── Flow Traffic Logs Modal ─────────────────────────────────────
function FlowLogsModal({ bId, fId, flowName, onClose }) {
  const [logs, setLogs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState('all'); // all | error
  const [q, setQ]             = useState('');
  const [expanded, setExpanded] = useState(null);
  const [liveTail, setLiveTail] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const liveTimer = useRef(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const data = await flowsApi(bId, { action: 'logs', fId, limit: 150 });
      if (data.success) setLogs(data.logs || []);
      else if (!silent) toast.error(data.message ?? 'Could not load logs');
    } catch {
      if (!silent) toast.error('Network error loading logs');
    }
    if (!silent) setLoading(false);
  }, [bId, fId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (liveTail) {
      liveTimer.current = setInterval(() => load(true), 4000);
      return () => clearInterval(liveTimer.current);
    }
  }, [liveTail, load]);

  async function handleClear() {
    try {
      const data = await flowsApi(bId, { action: 'clear_logs', fId });
      if (data.success) { setLogs([]); toast.success('Logs cleared'); }
      else toast.error(data.message ?? 'Could not clear logs');
    } catch {
      toast.error('Network error clearing logs');
    }
  }

  const errorCount = logs.filter(l => l.lError).length;
  const filtered = logs.filter(l => {
    if (filter === 'error' && !l.lError) return false;
    if (!q.trim()) return true;
    const hay = `${l.lAction ?? ''} ${l.lScreen ?? ''} ${l.lRequestBody ?? ''} ${l.lResponseBody ?? ''} ${l.lError ?? ''}`.toLowerCase();
    return hay.includes(q.trim().toLowerCase());
  });

  const Block = ({ title, text }) => (
    <div className="flx-log-block">
      <div className="flx-log-block-head">
        <span>{title}</span>
        <button type="button" className="flx-btn ghost xs" onClick={() => { copyToClipboard(text); toast.success('Copied'); }}><i className="fa-regular fa-copy" /> Copy</button>
      </div>
      <pre>{text}</pre>
    </div>
  );

  return (
    <Sheet icon="fa-wave-square" tone="dark" title="Traffic logs" wide className="is-logs"
      sub={<>{flowName}{!loading && logs.length > 0 && <> · {logs.length} call{logs.length !== 1 ? 's' : ''}{errorCount > 0 && <b className="flx-bad"> · {errorCount} failed</b>}</>}</>}
      onClose={onClose}>
      <div className="flx-logbar">
        <div className="flx-seg">
          <button type="button" className={filter === 'all' ? 'is-on' : ''} onClick={() => setFilter('all')}>All <span>{logs.length}</span></button>
          <button type="button" className={filter === 'error' ? 'is-on' : ''} onClick={() => setFilter('error')}>Errors <span className={errorCount ? 'is-bad' : ''}>{errorCount}</span></button>
        </div>
        <div className="flx-search">
          <i className="fa-solid fa-magnifying-glass" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search action, screen, payload…" />
        </div>
        <div className="flx-row">
          <button type="button" className={`flx-btn sm ${liveTail ? 'live' : 'ghost'}`} onClick={() => setLiveTail(v => !v)} title={liveTail ? 'Auto-refreshing every 4s' : 'Auto-refresh every 4s'}>
            <span className="flx-live-dot" /> {liveTail ? 'Live' : 'Live tail'}
          </button>
          <button type="button" className="flx-icon-btn bordered" onClick={() => load()} title="Refresh" aria-label="Refresh"><i className="fa-solid fa-rotate" /></button>
          <button type="button" className="flx-icon-btn bordered danger" onClick={() => setConfirmClear(true)} title="Clear logs" aria-label="Clear logs" disabled={!logs.length}><i className="fa-regular fa-trash-can" /></button>
        </div>
      </div>

      {confirmClear && (
        <div className="flx-inline-confirm">
          <span><i className="fa-solid fa-triangle-exclamation" /> Delete all {logs.length} logged calls for this flow?</span>
          <button type="button" className="flx-btn ghost sm" onClick={() => setConfirmClear(false)}>Cancel</button>
          <button type="button" className="flx-btn danger sm" onClick={() => { setConfirmClear(false); handleClear(); }}>Clear logs</button>
        </div>
      )}

      {loading ? (
        <div className="flx-skel">{[0, 1, 2, 3].map(i => <span key={i} />)}</div>
      ) : logs.length === 0 ? (
        <div className="flx-empty-mini">
          <i className="fa-solid fa-satellite-dish" />
          <b>No calls yet</b>
          <span>Meta hasn’t called this flow’s endpoint yet, or it has only sent health-check pings.</span>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flx-empty-mini"><i className="fa-solid fa-filter-circle-xmark" /><b>No logs match this filter</b></div>
      ) : (
        <div className="flx-logs">
          {filtered.map(log => {
            const isError = !!log.lError;
            const isOpen  = expanded === log.lId;
            return (
              <div key={log.lId} className={`flx-log ${isError ? 'is-err' : ''} ${isOpen ? 'is-open' : ''}`}>
                <button type="button" className="flx-log-row" onClick={() => setExpanded(isOpen ? null : log.lId)}>
                  <span className={`flx-log-tag ${isError ? 'is-err' : 'is-ok'}`}>{isError ? 'ERROR' : 'OK'}</span>
                  <span className="flx-log-main">
                    <b>{log.lAction || '(empty)'}</b>
                    {log.lScreen && <code>{log.lScreen}</code>}
                  </span>
                  <span className="flx-log-time">{formatDate(log.lCreatedAt)}{log.lDurationMs != null && <em>{log.lDurationMs}ms</em>}</span>
                  <i className={`fa-solid fa-chevron-${isOpen ? 'up' : 'down'}`} />
                </button>
                {isOpen && (
                  <div className="flx-log-body">
                    {log.lError && <div className="flx-log-err"><i className="fa-solid fa-circle-xmark" /> {log.lError}</div>}
                    {log.lEndpointUri && (
                      <div className="flx-log-route">
                        <span>Routed to</span>
                        <code>{log.lEndpointUri}</code>
                        {log.lProxyStatus != null && <em>HTTP {log.lProxyStatus}</em>}
                      </div>
                    )}
                    {log.lRequestBody && <Block title="Decrypted request" text={log.lRequestBody} />}
                    {log.lResponseBody && <Block title="Response sent back" text={log.lResponseBody} />}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Sheet>
  );
}

// ── Phone-frame preview ───────────────────────────────────────
function FlowPreview({ screens }) {
  const [screenIdx, setScreenIdx] = useState(0);
  const [completionInfo, setCompletionInfo] = useState(null);

  // Reset if the flow's screen count changes so a stale index never points past the end.
  useEffect(() => { setScreenIdx(0); setCompletionInfo(null); }, [screens?.length]);

  if (!screens || screens.length === 0) {
    return (
      <div className="flx-prev-empty">
        <div className="flx-prev-empty-phone"><i className="fa-solid fa-mobile-screen-button" /></div>
        <b>Nothing to preview yet</b>
        <span>Paste or write valid Flow JSON with at least one screen.</span>
      </div>
    );
  }
  const screen = screens[Math.min(screenIdx, screens.length - 1)];

  // "navigate" is simulated client-side. "complete" / "data_exchange" never call
  // your real endpoint from a preview — the payload that WOULD be sent is shown.
  function handleAction(action, dataSchema, answers) {
    if (!action || !action.name) return;
    const resolvedPayload = resolvePayloadObject(action.payload, dataSchema, answers);

    if (action.name === 'navigate' && action.next?.name) {
      const targetIdx = screens.findIndex(s => s.id === action.next.name);
      if (targetIdx === -1) {
        setCompletionInfo({ type: 'error', title: `Can't find screen "${action.next.name}"`, detail: 'The Footer\'s "next.name" doesn\'t match any screen "id" in this flow.' });
        return;
      }
      setCompletionInfo(null);
      setScreenIdx(targetIdx);
      return;
    }
    if (action.name === 'complete') {
      setCompletionInfo({ type: 'complete', title: 'Flow would complete here', detail: 'This sends the payload below to your endpoint. Not sent for real during preview.', payload: resolvedPayload });
      return;
    }
    if (action.name === 'data_exchange') {
      setCompletionInfo({ type: 'data_exchange', title: 'This screen calls your live endpoint', detail: 'data_exchange screens fetch the next screen from your server — not simulated here to avoid side effects (e.g. a real withdrawal). Use the health check to verify the endpoint.', payload: resolvedPayload });
      return;
    }
    if (action.name === 'open_url') {
      setCompletionInfo({ type: 'info', title: 'Would open a URL', detail: action.url ? String(action.url) : '(no url specified)' });
      return;
    }
    setCompletionInfo({ type: 'info', title: `Action: ${action.name}`, detail: 'This action type isn\'t simulated in preview yet.', payload: resolvedPayload });
  }

  const INFO_ICON = { complete: 'fa-flag-checkered', data_exchange: 'fa-plug', error: 'fa-circle-exclamation', info: 'fa-circle-info' };

  return (
    <div className="flx-prev">
      {screens.length > 1 && (
        <div className="flx-prev-screens">
          {screens.map((s, i) => (
            <button key={i} type="button" className={i === screenIdx ? 'is-on' : ''} onClick={() => { setScreenIdx(i); setCompletionInfo(null); }}>
              <span>{i + 1}</span>{s.id ?? `Screen ${i + 1}`}
            </button>
          ))}
        </div>
      )}
      {/* Card chrome matches Meta's WhatsApp Manager Flow preview: avatar + close. */}
      <div className="flx-phone" key={screen?.id}>
        <div className="flx-phone-notch" />
        <div className="flx-phone-chat">
          <div className="flx-phone-sheet">
            <div className="flx-phone-head">
              <span className="flx-phone-avatar" />
              <span className="flx-phone-title">{screen?.title ?? ''}</span>
              <i className="fa-solid fa-xmark" />
            </div>
            <div className="flx-phone-body">
              <ScreenRenderer screen={screen} onAction={handleAction} />
            </div>
          </div>
        </div>
      </div>
      {completionInfo && (
        <div className={`flx-prev-info is-${completionInfo.type}`}>
          <div className="flx-prev-info-head">
            <i className={`fa-solid ${INFO_ICON[completionInfo.type] ?? 'fa-circle-info'}`} />
            <b>{completionInfo.title}</b>
            <button type="button" className="flx-icon-btn sm" onClick={() => setCompletionInfo(null)} aria-label="Dismiss"><i className="fa-solid fa-xmark" /></button>
          </div>
          <p>{completionInfo.detail}</p>
          {completionInfo.payload && <pre>{JSON.stringify(completionInfo.payload, null, 2)}</pre>}
        </div>
      )}
    </div>
  );
}

// Renders a single screen's fields, tracks answers so the Continue
// button's enabled state reflects whether required fields are filled.
function ScreenRenderer({ screen, onAction }) {
  const [answers, setAnswers] = useState({});
  useEffect(() => { setAnswers({}); }, [screen?.id]);

  const dataSchema = screen?.data ?? {};
  const rootChildren = screen?.layout?.children ?? screen?.children ?? [];

  if (!rootChildren.length) return <div className="flx-pv-none">No components defined</div>;

  const footer = findFooter(rootChildren);
  const requiredNames = collectRequiredNames(rootChildren);
  const allRequiredFilled = requiredNames.every(n => {
    const v = answers[n];
    return v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0);
  });

  function setAnswer(name, value) {
    setAnswers(a => ({ ...a, [name]: value }));
  }

  return (
    <>
      <div className="flx-pv-fields">
        {rootChildren.map((c, i) => (
          <FlowComponent key={i} comp={c} dataSchema={dataSchema} answers={answers} setAnswer={setAnswer} />
        ))}
      </div>
      <div className="flx-pv-footer">
        <button type="button" className={`flx-pv-cta ${allRequiredFilled ? 'is-on' : ''}`} disabled={!allRequiredFilled}
          onClick={() => onAction?.(footer?.['on-click-action'], dataSchema, answers)}>
          {footer?.label ? resolveText(footer.label, dataSchema) : 'Continue'}
        </button>
        <div className="flx-pv-managed">Managed by the business. <span>Learn more</span></div>
      </div>
    </>
  );
}

// Footer is usually nested inside a Form; walk the tree to find it.
function findFooter(children) {
  for (const c of children) {
    const type = (c.type ?? '').toLowerCase();
    if (type === 'footer') return c;
    if (Array.isArray(c.children)) {
      const found = findFooter(c.children);
      if (found) return found;
    }
  }
  return null;
}

// Best-effort: collects the `name` of every component marked required:true.
function collectRequiredNames(children) {
  let names = [];
  for (const c of children) {
    if (c.required === true && c.name) names.push(c.name);
    if (Array.isArray(c.children)) names = names.concat(collectRequiredNames(c.children));
  }
  return names;
}

function PvLabel({ comp }) {
  if (!comp.label) return null;
  return <div className="flx-pv-label">{comp.label}{comp.required && <span>*</span>}</div>;
}

function FlowComponent({ comp, dataSchema, answers, setAnswer }) {
  const type = (comp.type ?? '').toLowerCase();

  if (type === 'form') {
    const inner = comp.children ?? [];
    return <div className="flx-pv-form">
      {inner.map((c, i) => <FlowComponent key={i} comp={c} dataSchema={dataSchema} answers={answers} setAnswer={setAnswer} />)}
    </div>;
  }
  if (type === 'textheading' || type === 'text-heading') return <div className="flx-pv-h1">{resolveText(comp.text, dataSchema)}</div>;
  if (type === 'textsubheading' || type === 'text-subheading') return <div className="flx-pv-h2">{resolveText(comp.text, dataSchema)}</div>;
  if (type === 'textbody' || type === 'text-body' || type === 'text') return <div className="flx-pv-text">{resolveText(comp.text, dataSchema)}</div>;
  if (type === 'textcaption' || type === 'text-caption') return <div className="flx-pv-caption">{resolveText(comp.text, dataSchema)}</div>;
  if (type === 'textinput' || type === 'text-input') {
    return (
      <div className="flx-pv-float">
        <input className="flx-pv-input" placeholder=" " value={answers[comp.name] ?? ''} onChange={e => setAnswer(comp.name, e.target.value)}
          type={comp['input-type'] === 'number' || comp['input-type'] === 'phone' ? 'tel' : comp['input-type'] === 'password' ? 'password' : 'text'} />
        {comp.label && <label>{comp.label}{comp.required && <span>*</span>}</label>}
        {comp['helper-text'] && <em>{comp['helper-text']}</em>}
      </div>
    );
  }
  if (type === 'textarea') {
    return (
      <div className="flx-pv-float">
        <textarea className="flx-pv-input is-area" placeholder=" " value={answers[comp.name] ?? ''} onChange={e => setAnswer(comp.name, e.target.value)} />
        {comp.label && <label>{comp.label}{comp.required && <span>*</span>}</label>}
        {comp['helper-text'] && <em>{comp['helper-text']}</em>}
      </div>
    );
  }
  if (type === 'radiobuttons' || type === 'radio-buttons' || type === 'radiobuttonsgroup' || type === 'radio-buttons-group') {
    const opts = asArray(comp['data-source'] ?? comp.options);
    const selected = answers[comp.name];
    return (
      <div>
        <PvLabel comp={comp} />
        <div className="flx-pv-opts">
          {opts.map((o, i) => {
            const val = o.id ?? o.value ?? o.title ?? o.label ?? o;
            const label = o.title ?? o.label ?? String(o);
            return (
              <button key={i} type="button" className="flx-pv-opt" onClick={() => setAnswer(comp.name, val)}>
                <span className="flx-pv-opt-txt">{label}{o.description && <em>{o.description}</em>}</span>
                <span className={`flx-pv-radio ${selected === val ? 'is-on' : ''}`} />
              </button>
            );
          })}
        </div>
      </div>
    );
  }
  if (type === 'checkboxes' || type === 'checkbox-group' || type === 'checkboxgroup') {
    const opts = asArray(comp['data-source'] ?? comp.options);
    const selected = answers[comp.name] ?? [];
    return (
      <div>
        <PvLabel comp={comp} />
        <div className="flx-pv-opts">
          {opts.map((o, i) => {
            const val = o.id ?? o.value ?? o.title ?? o.label ?? o;
            const label = o.title ?? o.label ?? String(o);
            const isChecked = Array.isArray(selected) && selected.includes(val);
            return (
              <button key={i} type="button" className="flx-pv-opt" onClick={() => {
                setAnswer(comp.name, isChecked ? selected.filter(v => v !== val) : [...selected, val]);
              }}>
                <span className="flx-pv-opt-txt">{label}{o.description && <em>{o.description}</em>}</span>
                <span className={`flx-pv-check ${isChecked ? 'is-on' : ''}`}><i className="fa-solid fa-check" /></span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }
  if (type === 'dropdown') {
    const opts = asArray(comp['data-source'] ?? comp.options);
    return (
      <div className="flx-pv-float is-select">
        <select className="flx-pv-input" value={answers[comp.name] ?? ''} onChange={e => setAnswer(comp.name, e.target.value)}>
          <option value="" disabled>{' '}</option>
          {opts.map((o, i) => {
            const val = o.id ?? o.value ?? o.title ?? o.label ?? o;
            return <option key={i} value={val}>{o.title ?? o.label ?? String(o)}</option>;
          })}
        </select>
        {comp.label && <label className={answers[comp.name] ? 'is-up' : ''}>{comp.label}{comp.required && <span>*</span>}</label>}
        <i className="fa-solid fa-chevron-down" />
      </div>
    );
  }
  if (type === 'datepicker' || type === 'date-picker') {
    return (
      <div className="flx-pv-float is-date">
        <input type="date" className="flx-pv-input" value={answers[comp.name] ?? ''} onChange={e => setAnswer(comp.name, e.target.value)} />
        {comp.label && <label className="is-up">{comp.label}{comp.required && <span>*</span>}</label>}
      </div>
    );
  }
  if (type === 'optin' || type === 'opt-in') {
    const on = !!answers[comp.name];
    return (
      <button type="button" className="flx-pv-optin" onClick={() => setAnswer(comp.name, on ? '' : true)}>
        <span className={`flx-pv-check ${on ? 'is-on' : ''}`}><i className="fa-solid fa-check" /></span>
        <span>{resolveText(comp.label, dataSchema)}{comp.required && <b>*</b>}</span>
      </button>
    );
  }
  if (type === 'embeddedlink' || type === 'embedded-link') return <div className="flx-pv-link">{resolveText(comp.text, dataSchema)}</div>;
  if (type === 'image') {
    return comp.src && /^data:image\//.test(String(comp.src))
      ? <img className="flx-pv-img" src={comp.src} alt={comp['alt-text'] ?? ''} />
      : <div className="flx-pv-imgph"><i className="fa-regular fa-image" /> Image</div>;
  }
  if (type === 'footer') return null; // rendered by ScreenRenderer, pinned below the scrollable body
  return <div className="flx-pv-unknown">[{comp.type ?? 'unknown'}]</div>;
}

// Substitutes ${data.xxx} with that field's __example__ value from the
// screen's own `data` schema; anything unresolved stays visible.
function resolveText(val, dataSchema) {
  if (val === undefined || val === null) return '';
  if (typeof val !== 'string') return String(val);
  if (!dataSchema) return val;
  return val.replace(/\$\{data\.([a-zA-Z0-9_]+)\}/g, (match, key) => {
    const field = dataSchema[key];
    if (field && typeof field === 'object' && '__example__' in field) {
      return String(field.__example__);
    }
    return match;
  });
}

// Resolves a Footer action's "payload" for preview — ${data.x} from the
// screen schema and ${form.x} from what was typed in this screen.
function resolvePayloadObject(payload, dataSchema, answers) {
  if (payload === null || payload === undefined) return payload;
  if (typeof payload === 'string') {
    const formMatch = payload.match(/^\$\{form\.([a-zA-Z0-9_]+)\}$/);
    if (formMatch) {
      const v = answers?.[formMatch[1]];
      return v === undefined ? '' : v;
    }
    return resolveText(payload, dataSchema);
  }
  if (Array.isArray(payload)) return payload.map(v => resolvePayloadObject(v, dataSchema, answers));
  if (typeof payload === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(payload)) out[k] = resolvePayloadObject(v, dataSchema, answers);
    return out;
  }
  return payload;
}

// ── Flow list card ────────────────────────────────────────────
function FlowCard({ flow, bId, onEdit, onRefresh }) {
  const [busy,    setBusy]    = useState('');
  const [confirm, setConfirm] = useState(null);

  async function doAction(action, label) {
    setBusy(action);
    try {
      const data = await flowsApi(bId, { action, fId: flow.fId });
      if (data.success) { toast.success(`${label} successful`); onRefresh(); }
      else toast.error(data.message ?? `${label} failed`);
    } catch { toast.error('Network error'); }
    setBusy('');
  }

  const m = catMeta(flow.fCategory ?? 'OTHER');
  const status = flow.fStatus ?? 'draft';
  let screens = null;
  try { screens = flow.fJsonDef ? (JSON.parse(flow.fJsonDef)?.screens?.length ?? null) : null; } catch { screens = null; }

  return (
    <>
      <article className={`flx-card-flow is-${status}`}>
        <div className="flx-card-flow-top" role="button" tabIndex={0} onClick={() => onEdit(flow)}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEdit(flow); } }} title="Open editor">
          <span className="flx-flow-ic"><i className={`fa-solid ${m.icon}`} /></span>
          <div className="flx-flow-main">
            <h4>{flow.fName}</h4>
            <div className="flx-flow-meta">
              <span>{m.label}</span>
              {screens != null && <span><i className="fa-regular fa-clone" /> {screens} screen{screens !== 1 ? 's' : ''}</span>}
              {flow.fEndpointUri && <span title={flow.fEndpointUri}><i className="fa-solid fa-plug" /> Endpoint</span>}
            </div>
          </div>
          <StatusBadge status={status} />
        </div>
        <div className="flx-card-flow-foot">
          <span className="flx-flow-date"><i className="fa-regular fa-clock" /> {fmtShortDate(flow.fUpdatedAt)}</span>
          {flow.fMetaFlowId ? <CopyChip value={flow.fMetaFlowId} label={`ID ${flow.fMetaFlowId}`} /> : <span className="flx-chip is-muted">Not on Meta</span>}
          <span className="flx-grow" />
          {status === 'draft' && flow.fMetaFlowId && (
            <button type="button" className="flx-btn primary sm" disabled={!!busy}
              onClick={() => setConfirm({ action: 'publish', label: 'Publish', title: 'Publish this flow?', message: 'Once published, this flow can’t be edited. Make sure everything looks right.', detail: flow.fName, variant: 'warning' })}>
              {busy === 'publish' ? <Spinner light /> : <i className="fa-solid fa-rocket" />} Publish
            </button>
          )}
          {status === 'published' && (
            <button type="button" className="flx-btn ghost sm" disabled={!!busy}
              onClick={() => setConfirm({ action: 'deprecate', label: 'Deprecate', title: 'Deprecate this flow?', message: 'Customers won’t be able to open this flow any more.', detail: flow.fName, variant: 'warning' })}>
              {busy === 'deprecate' ? <Spinner /> : <i className="fa-solid fa-box-archive" />} Deprecate
            </button>
          )}
          <button type="button" className="flx-btn ghost sm" onClick={() => onEdit(flow)}><i className="fa-solid fa-pen-to-square" /> Open</button>
          <button type="button" className="flx-icon-btn bordered danger" aria-label="Delete flow"
            title={status === 'published' ? 'Deprecate a published flow before deleting it' : 'Delete'}
            onClick={() => setConfirm({ action: 'delete', label: 'Delete', title: 'Delete this flow?', message: 'This permanently removes the flow from Meta and can’t be undone.', detail: flow.fName, variant: 'danger' })}
            disabled={!!busy || status === 'published'}>
            {busy === 'delete' ? <Spinner /> : <i className="fa-regular fa-trash-can" />}
          </button>
        </div>
      </article>
      {confirm && (
        <ConfirmModal title={confirm.title} message={confirm.message} detail={confirm.detail} confirmLabel={confirm.label}
          variant={confirm.variant} onConfirm={() => doAction(confirm.action, confirm.label)} onClose={() => setConfirm(null)} />
      )}
    </>
  );
}

// ── Main FlowsTab ─────────────────────────────────────────────
export default function FlowsTab({ bId, bot }) {
  const [flows,        setFlows]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [syncing,      setSyncing]      = useState(false);
  const [showCreate,   setShowCreate]   = useState(false);
  const [editing,      setEditing]      = useState(null);
  const [search,       setSearch]       = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [isRental,      setIsRental]      = useState(false);
  const [defaultEndpoint, setDefaultEndpoint] = useState('');
  const [hasKey,        setHasKey]        = useState(null); // HEALTH, not existence (null = not checked yet)
  const [keyExists,     setKeyExists]     = useState(false); // does *something* exist that generating would overwrite
  const [keyCheckIssue, setKeyCheckIssue] = useState(''); // set when the check itself failed
  const [showGenerateModal, setShowGenerateModal] = useState(false);

  const hasWaba = !!(bot?.bWhatsappId);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await flowsGet(bId);
      if (data.success) {
        setFlows(data.flows ?? []);
        setIsRental(!!data.isRental);
        setDefaultEndpoint(data.defaultEndpoint || '');
      } else toast.error(data.message ?? 'Failed to load flows');
    } catch { toast.error('Network error'); }
    setLoading(false);
  }, [bId]);

  // Encryption key is per-bot (tied to the phone number), so check it once here.
  // Any failure still shows a banner, with the backend's reason.
  const checkKey = useCallback(async () => {
    try {
      const data = await flowsApi(bId, { action: 'get_encryption_status' });
      if (data.success) {
        const healthy = isEncryptionKeyHealthy(data);
        setHasKey(healthy);
        // Independent of `healthy`: a corrupted-but-present key must still count
        // as "exists" so GenerateKeyModal shows its force-replace warning.
        setKeyExists(!!data.has_key || !!data.local_key_exists);
        setKeyCheckIssue(
          !healthy && data.has_key && UNHEALTHY_KEY_STATUSES.includes(data.key_status)
            ? `Meta has a key on file, but reports its status as "${data.key_status}" — it needs to be replaced.`
            : ''
        );
      } else {
        setHasKey(false);
        setKeyCheckIssue(data.message || 'Could not check encryption status.');
      }
    } catch {
      setHasKey(false);
      setKeyCheckIssue('Could not check encryption status (network error).');
    }
  }, [bId]);

  useEffect(() => { load(); checkKey(); }, [load, checkKey]);

  async function syncFromMeta() {
    if (!hasWaba) return toast.error('Set your WABA ID in Bot Settings first');
    setSyncing(true);
    try {
      const data = await flowsApi(bId, { action: 'sync_from_meta' });
      if (data.success) {
        // On a shared Rent Space WABA, `skipped` counts flows belonging to other renters.
        const skipped = data.skipped ?? 0;
        toast.success(
          skipped > 0
            ? `Synced ${data.synced ?? 0} of your flow(s) (${skipped} from other renters skipped)`
            : `Synced ${data.synced ?? 0} flow(s) from Meta`
        );
        load();
      } else {
        toast.error(data.message ?? 'Sync failed');
      }
    } catch { toast.error('Network error'); }
    setSyncing(false);
  }

  const counts = {
    '': flows.length,
    draft: flows.filter(f => (f.fStatus ?? 'draft') === 'draft').length,
    published: flows.filter(f => f.fStatus === 'published').length,
    deprecated: flows.filter(f => f.fStatus === 'deprecated').length,
  };

  const filtered = flows.filter(f => {
    const matchSearch = !search || f.fName.toLowerCase().includes(search.toLowerCase())
      || (f.fMetaFlowId ?? '').includes(search);
    const matchStatus = !filterStatus || (f.fStatus ?? 'draft') === filterStatus;
    return matchSearch && matchStatus;
  });

  if (editing) {
    return (
      <FlowEditor
        flow={editing}
        bId={bId}
        bot={bot}
        isRental={isRental}
        defaultEndpoint={defaultEndpoint}
        onClose={() => { setEditing(null); load(); }}
        onSaved={(updatedFlow) => { if (updatedFlow) setEditing(updatedFlow); load(); }}
      />
    );
  }

  const missingPhoneId = /^bot has no phone number id/i.test(keyCheckIssue);

  return (
    <div className="flx">
      {/* ── Hero ── */}
      <section className="flx-hero">
        <div className="flx-hero-copy">
          <span className="flx-hero-ic"><i className="fa-solid fa-table-list" /></span>
          <div>
            <h2>WhatsApp Flows</h2>
            <p>Forms, sign-ups and bookings that open right inside WhatsApp.</p>
          </div>
        </div>
        <div className="flx-hero-actions">
          <button type="button" className="flx-btn on-dark" onClick={syncFromMeta} disabled={syncing || !hasWaba}
            title={isRental ? 'Refresh flows you created here — other renters’ flows are never pulled in' : 'Pull existing flows from Meta into your account'}>
            {syncing ? <Spinner light /> : <i className="fa-solid fa-arrows-rotate" />} <span>{syncing ? 'Syncing…' : 'Sync from Meta'}</span>
          </button>
          <button type="button" className="flx-btn primary" onClick={() => setShowCreate(true)} disabled={!hasWaba}>
            <i className="fa-solid fa-plus" /> <span>New flow</span>
          </button>
        </div>
        <div className="flx-hero-stats">
          {[['', 'All flows', 'fa-layer-group'], ['published', 'Live', 'fa-circle-check'], ['draft', 'Drafts', 'fa-pen'], ['deprecated', 'Deprecated', 'fa-box-archive']].map(([k, l, ic]) => (
            <button key={k || 'all'} type="button" className={`flx-hero-stat ${filterStatus === k ? 'is-on' : ''}`} onClick={() => setFilterStatus(k)}>
              <i className={`fa-solid ${ic}`} />
              <b>{loading ? '–' : counts[k]}</b><span>{l}</span>
            </button>
          ))}
          <div className={`flx-hero-key ${hasKey ? 'is-ok' : hasKey === false ? 'is-bad' : ''}`}>
            <i className={`fa-solid ${hasKey ? 'fa-shield-halved' : hasKey === false ? 'fa-lock-open' : 'fa-circle-notch fa-spin'}`} />
            <span>{hasKey ? 'Encryption ready' : hasKey === false ? 'Encryption not set up' : 'Checking encryption…'}</span>
          </div>
        </div>
      </section>

      {/* ── Setup notices ── */}
      {!hasWaba && (
        <Note tone="warn">
          <b>WABA ID not set.</b> Add your WhatsApp Business Account ID in <b>Overview → Settings</b> to create or sync flows.
        </Note>
      )}
      {hasWaba && hasKey === false && (
        missingPhoneId ? (
          <Note tone="danger">
            <b>This bot is missing its Phone Number ID.</b> That has to be fixed on the account before an encryption key can be
            set up — please contact support.
          </Note>
        ) : (
          <Note tone="danger"
            action={<button type="button" className="flx-btn danger sm" onClick={() => setShowGenerateModal(true)}><i className="fa-solid fa-key" /> Set up encryption</button>}>
            <b>No encryption key yet.</b>{keyCheckIssue ? ` ${keyCheckIssue}` : ''} Flows that load data or run a health check on this
            number will fail until this one-time step is done — it takes a second, no technical setup needed.
          </Note>
        )
      )}
      {isRental && (
        <Note tone="info">
          This number is on a shared Rent Space account. <b>Sync from Meta</b> only refreshes flows <b>you</b> created here — never
          other renters’ flows.
        </Note>
      )}

      {/* ── Toolbar ── */}
      {flows.length > 0 && (
        <div className="flx-toolbar">
          <div className="flx-search">
            <i className="fa-solid fa-magnifying-glass" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or Meta ID…" aria-label="Search flows" />
            {search && <button type="button" onClick={() => setSearch('')} aria-label="Clear search"><i className="fa-solid fa-xmark" /></button>}
          </div>
          <div className="flx-seg">
            {[['', 'All'], ['published', 'Live'], ['draft', 'Draft'], ['deprecated', 'Deprecated']].map(([k, l]) => (
              <button key={k || 'all'} type="button" className={filterStatus === k ? 'is-on' : ''} onClick={() => setFilterStatus(k)}>
                {l} <span>{counts[k]}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── List ── */}
      {loading ? (
        <div className="flx-grid">{[0, 1, 2, 3].map(i => <div key={i} className="flx-card-skel"><span /><span /><span /></div>)}</div>
      ) : flows.length === 0 ? (
        <section className="flx-empty">
          <div className="flx-empty-art">
            <div className="flx-empty-phone">
              <span className="l1" /><span className="l2" /><span className="f" /><span className="f" /><span className="cta" />
            </div>
          </div>
          <h3>No flows yet</h3>
          <p>
            {!hasWaba
              ? 'Set your WABA ID in bot settings first, then create a flow.'
              : isRental
                ? 'Create a new flow, or sync flows you’ve already made here.'
                : 'Create your first flow, or bring in the ones you already have on Meta.'}
          </p>
          {hasWaba && (
            <div className="flx-row center">
              <button type="button" className="flx-btn ghost" onClick={syncFromMeta} disabled={syncing}>
                {syncing ? <Spinner /> : <i className="fa-solid fa-arrows-rotate" />} {syncing ? 'Syncing…' : 'Sync from Meta'}
              </button>
              <button type="button" className="flx-btn primary" onClick={() => setShowCreate(true)}><i className="fa-solid fa-plus" /> Create flow</button>
            </div>
          )}
        </section>
      ) : filtered.length === 0 ? (
        <div className="flx-empty-mini">
          <i className="fa-solid fa-magnifying-glass" />
          <b>No flows match</b>
          <button type="button" className="flx-link" onClick={() => { setSearch(''); setFilterStatus(''); }}>Clear filters</button>
        </div>
      ) : (
        <div className="flx-grid">
          {filtered.map(f => (
            <FlowCard key={f.fId} flow={f} bId={bId} onEdit={setEditing} onRefresh={load} />
          ))}
        </div>
      )}

      {hasWaba && flows.length > 0 && (
        <button type="button" className="flx-fab" onClick={() => setShowCreate(true)} aria-label="New flow"><i className="fa-solid fa-plus" /></button>
      )}

      {showCreate && (
        <CreateFlowModal bId={bId} hasWaba={hasWaba} isRental={isRental} defaultEndpoint={defaultEndpoint}
          onClose={() => setShowCreate(false)} onCreated={load} />
      )}
      {showGenerateModal && (
        <GenerateKeyModal bId={bId} hasExistingKey={keyExists}
          onClose={() => setShowGenerateModal(false)}
          onGenerated={() => { checkKey(); }} />
      )}
    </div>
  );
}